// features/customer/components/CampaignsAndEvents.tsx
import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import simbDemoData from "../../../src/mocks/simbDemoData.json";
import { API_ORIGIN, API_ROOT_URL, API_V1_BASE_URL } from "../../config/api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useLocation } from "react-router-dom";

function useDebouncedValue<T>(value: T, delay = 400): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

// LibraryPage.tsx renders an empty bar with this id between the app header and
// the "Library" heading. The Event / Product / Asset detail pages render their
// breadcrumb into it. Keep this id in sync with BREADCRUMB_SLOT_ID there.
const LIBRARY_BREADCRUMB_SLOT_ID = "library-breadcrumb-slot";

function useLibraryBreadcrumbSlot(): HTMLElement | null {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setSlot(document.getElementById(LIBRARY_BREADCRUMB_SLOT_ID));
  }, []);
  return slot;
}

const isValidUUID = (value: unknown): value is string =>
  typeof value === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

const authenticatedJsonHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("accessToken") || ""}`,
});

// Styled replacement for window.confirm — renders inside react-toastify
// instead of the native browser dialog.
const confirmToast = (message: string): Promise<boolean> => {
  return new Promise((resolve) => {
    toast(
      ({ closeToast }) => (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-gray-800">{message}</p>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                closeToast?.();
                resolve(false);
              }}
              className="px-3 py-1.5 text-sm rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                closeToast?.();
                resolve(true);
              }}
              className="px-3 py-1.5 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700"
            >
              Delete
            </button>
          </div>
        </div>
      ),
      {
        autoClose: false,
        closeOnClick: false,
        closeButton: false,
        draggable: false,
        position: "top-center",
      },
    );
  });
};

const getLocalToday = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Backend may return 'ACTIVE' / 'Active' / 'ARCHIVED' etc. Normalise for the UI.
const normalizeStatus = (s: unknown): string => {
  const raw = String(s ?? "").trim();
  const v = raw.toUpperCase();
  if (v === "ACTIVE") return "Active";
  if (v === "ARCHIVED") return "Archived";
  if (v === "SCHEDULED") return "Scheduled";
  if (v === "INACTIVE") return "Inactive"; // <--- ADD THIS LINE
  return raw;
};

// Theme-aware tokens consumed by the Create Campaign modal.
// Values come from ThemeProvider (document.documentElement.style.setProperty).
const THEME_PRIMARY = "var(--color-primary, #059669)";
const THEME_PRIMARY_SOFT =
  "color-mix(in srgb, var(--color-primary, #059669) 12%, white)";
const THEME_PRIMARY_RING =
  "color-mix(in srgb, var(--color-primary, #059669) 45%, transparent)";

// Base URL for the event ↔ product/asset link endpoints (/event-products, /event-assets).
// Change this one line if the backend mounts them elsewhere (e.g. API_ORIGIN, like /events).
const EVENT_LINKS_BASE_URL = API_ROOT_URL;

// ----- Asset display helpers (event / product / asset pages) -----
const isVideoAsset = (name = "", container?: string | null) =>
  /\.(mp4|mov|webm|mkv|avi|m4v)$/i.test(name) ||
  ["mp4", "mov", "webm", "mkv", "avi", "quicktime"].includes(
    (container || "").toLowerCase(),
  );

const isImageAsset = (name = "", container?: string | null) =>
  /\.(jpe?g|png|gif|webp|bmp|svg|avif)$/i.test(name) ||
  ["jpg", "jpeg", "png", "gif", "webp", "image"].includes(
    (container || "").toLowerCase(),
  );

// Find a viewable link on an asset record. The API's field name isn't fixed
// (snake_case, camelCase or nested), so try known names first, then any
// "...url..." field holding a link. Thumbnails are only used when
// `allowThumbnail` is true (fine for images, wrong for videos).
const MEDIA_URL_KEYS = [
  "playback_url", "playbackUrl",
  "preview_url", "previewUrl",
  "proxy_url", "proxyUrl",
  "signed_url", "signedUrl",
  "stream_url", "streamUrl",
  "download_url", "downloadUrl",
  "file_url", "fileUrl",
  "image_url", "imageUrl",
  "master_url", "masterUrl",
  "source_url", "sourceUrl",
  "url",
];

const isLink = (v: unknown): v is string =>
  typeof v === "string" && /^(https?:|blob:|data:)/.test(v);

const pickMediaUrl = (
  asset: Record<string, any> | null | undefined,
  allowThumbnail = false,
): string => {
  if (!asset || typeof asset !== "object") return "";
  const isThumb = (k: string) => /thumb/i.test(k);

  for (const key of MEDIA_URL_KEYS) {
    if (isLink(asset[key])) return asset[key];
  }
  // Any other "...url..." field
  for (const [k, v] of Object.entries(asset)) {
    if (/url/i.test(k) && !isThumb(k) && isLink(v)) return v;
  }
  // One level of nesting, e.g. { preview: { url } } or { urls: { proxy } }
  for (const [k, v] of Object.entries(asset)) {
    if (v && typeof v === "object" && !Array.isArray(v) && (allowThumbnail || !isThumb(k))) {
      const nested = pickMediaUrl(v, allowThumbnail);
      if (nested) return nested;
    }
  }
  // Last resort for images: the thumbnail link
  if (allowThumbnail) {
    for (const [k, v] of Object.entries(asset)) {
      if (isThumb(k) && isLink(v)) return v;
    }
  }
  return "";
};

const formatBytes = (n?: number | null) => {
  if (!n || n <= 0) return "";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), units.length - 1);
  return `${(n / 1024 ** i).toFixed(i ? 1 : 0)} ${units[i]}`;
};

const formatDuration = (sec?: number | null) => {
  if (!sec || sec <= 0) return "";
  const s = Math.round(sec);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
};

interface CampaignAsset {
  id: string;
  url: string;
  type: "image" | "video" | "document";
  title: string;
  order: number;
}

interface Campaign {
  id: string;
  title: string;
  description?: string;
  startDate: string;
  endDate: string;
  notes: string;
  tags: string[];
  status: "Active" | "Archived";
  image: string;
  video?: string;
  // Legacy fields kept for compatibility
  message?: string;
  externalReference?: string;
  versionNumber?: number;
  buttonText?: string;
  buttonLink?: string;
  location?: string;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
  approvedBy?: string;
  approvalStatus?: string;
  approvedAt?: string;
  isVisible?: boolean;
  order?: number;
  assets?: CampaignAsset[];
  eventIds?: string[]; // NEW — linked event UUIDs
  assetIds?: string[]; // NEW — linked asset IDs
}

interface Product {
  id: string;
  name: string;
  description?: string;
  launchDate: string;
  endDate?: string;
  notes: string;
  tags: string[];
  status: "Active" | "Archived";
  image: string;
  video?: string;
  assetIds?: string[]; // NEW — linked asset IDs
}

interface Event {
  id: string;
  eventName: string;
  description?: string;
  eventDate: string;
  endDate?: string;
  notes: string;
  tags: string[];
  status: "Active" | "Archived" | "Scheduled" | "Inactive"; // <--- ADDED Inactive HERE
  image: string;
  video?: string;
  productIds?: string[]; // linked product IDs
  assetIds?: string[]; // linked asset IDs
}

interface Asset {
  id: string;
  original_filename: string;
  source_key: string;
  master_key?: string | null;
  proxy_key?: string | null;
  checksum?: string | null;
  status:
  | "received"
  | "validating"
  | "rejected"
  | "queued"
  | "transcoding"
  | "completed"
  | "failed"
  | "archived";
  codec?: string | null;
  container?: string | null;
  width?: number | null;
  height?: number | null;
  duration_sec?: number | null;
  file_size_bytes?: number | null;
  created_at?: string;
  updated_at?: string;
  product_id?: string | null;

  // Demo JSON fields
  preview_url?: string | null;
  mime_type?: string | null;
  kind?: string;
}

interface ArchiveCounts {
  campaigns: number;
  products: number;
  events: number;
  total: number;
}

type ArchiveVisibility = "Public" | "Private";

type CampaignsView = "summary" | "campaigns" | "products" | "events";

interface CampaignsAndEventsProps {
  tenant?: any;
  view?: CampaignsView; // when set, locks the component to one section and hides the internal tab switcher
}

interface EventMultiSelectProps {
  events: Event[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  onRemove: (id: string) => void;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
}

const EventMultiSelect: React.FC<EventMultiSelectProps> = ({
  events,
  selectedIds,
  onToggle,
  onRemove,
  isOpen,
  setIsOpen,
  searchQuery,
  setSearchQuery,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [setIsOpen]);

  const filteredEvents = events.filter((ev) =>
    ev.eventName.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <div className="relative" ref={containerRef}>
      {/* Chip field / trigger */}
      <div
        className="w-full min-h-[42px] p-2 border border-gray-300 rounded-lg bg-white cursor-text flex flex-wrap gap-2 items-center focus-within:ring-2 focus-within:ring-purple-500"
        onClick={() => setIsOpen(true)}
      >
        {selectedIds.length === 0 && (
          <span className="text-gray-400 text-sm px-1">Select events...</span>
        )}
        {selectedIds.map((id) => {
          const ev = events.find((e) => e.id === id);
          if (!ev) return null;
          return (
            <span
              key={id}
              className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 text-sm rounded-full"
            >
              {ev.eventName}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(id);
                }}
                className="text-purple-400 hover:text-purple-700"
              >
                ×
              </button>
            </span>
          );
        })}
      </div>

      {/* Dropdown panel */}
      {isOpen && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 flex flex-col">
          <div className="p-2 border-b border-gray-200 relative">
            <input
              type="text"
              autoFocus
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search events..."
              className="w-full px-3 py-2 pr-8 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 text-sm"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>
          <div className="overflow-y-auto">
            {filteredEvents.length === 0 ? (
              <p className="text-sm text-gray-400 p-3">No events found</p>
            ) : (
              filteredEvents.map((ev) => (
                <label
                  key={ev.id}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(ev.id)}
                    onChange={() => onToggle(ev.id)}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="text-gray-700">{ev.eventName}</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

interface AssetChipFieldProps {
  assets: Asset[];
  selectedIds: string[];
  onRemove: (id: string) => void;
  onOpenPicker: () => void;
}

const AssetChipField: React.FC<AssetChipFieldProps> = ({
  assets,
  selectedIds,
  onRemove,
  onOpenPicker,
}) => {
  return (
    <div
      className="w-full min-h-[42px] p-2 border border-gray-300 rounded-lg bg-white cursor-pointer flex flex-wrap gap-2 items-center hover:border-purple-400 transition"
      onClick={onOpenPicker}
    >
      {selectedIds.length === 0 && (
        <span className="text-gray-400 text-sm px-1">
          Click to select assets...
        </span>
      )}
      {selectedIds.map((id) => {
        const asset = assets.find((a) => a.id === id);
        if (!asset) return null;
        return (
          <span
            key={id}
            className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 text-sm rounded-full"
          >
            {asset.original_filename}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRemove(id);
              }}
              className="text-purple-400 hover:text-purple-700"
            >
              ×
            </button>
          </span>
        );
      })}
    </div>
  );
};

interface ProductAssetGroup {
  product_id: string;
  product_title: string;
  assets: {
    id: string;
    original_filename: string;
    status?: string;
    container?: string | null;
  }[];
}

interface AssetPickerModalProps {
  assets: Asset[];
  assetsLoading: boolean;
  selectedIds: string[];
  onToggle: (id: string) => void;
  onClose: () => void;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  // Campaign mode (all optional). Passing productGroups turns on the
  // "mapped to products only" notice and the All assets / By product tabs.
  productGroups?: ProductAssetGroup[];
  productGroupsLoading?: boolean;
  onSetMany?: (ids: string[], select: boolean) => void;
}

const AssetPickerModal: React.FC<AssetPickerModalProps> = ({
  assets,
  assetsLoading,
  selectedIds,
  onToggle,
  onClose,
  searchQuery,
  setSearchQuery,
  productGroups,
  productGroupsLoading = false,
  onSetMany,
}) => {
  const campaignMode = productGroups !== undefined;
  const [mode, setMode] = useState<"all" | "product">("all");
  const [openIds, setOpenIds] = useState<string[]>([]);

  const term = searchQuery.trim().toLowerCase();
  const filtered = assets.filter((a) =>
    a.original_filename.toLowerCase().includes(term),
  );
  // Selected assets float to the top so the user sees their current picks first
  const sorted = [...filtered].sort((a, b) => {
    const aSel = selectedIds.includes(a.id) ? 0 : 1;
    const bSel = selectedIds.includes(b.id) ? 0 : 1;
    return aSel - bSel;
  });

  // By-product view: match on product name, or on any asset name inside it
  const visibleGroups = (productGroups || [])
    .map((g) => {
      const productMatches = g.product_title.toLowerCase().includes(term);
      return {
        ...g,
        assets:
          productMatches || !term
            ? g.assets
            : g.assets.filter((a) =>
              a.original_filename.toLowerCase().includes(term),
            ),
      };
    })
    .filter((g) => g.assets.length > 0);

  const toggleGroup = (id: string) =>
    setOpenIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const renderAssetRow = (id: string, name: string, meta?: string | null) => (
    <label
      key={id}
      className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer border border-transparent hover:bg-gray-50 hover:border-gray-200"
    >
      <input
        type="checkbox"
        checked={selectedIds.includes(id)}
        onChange={() => onToggle(id)}
        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-800 truncate">{name}</p>
        {meta && <p className="text-xs text-gray-400">{meta}</p>}
      </div>
    </label>
  );

  const showProductView = campaignMode && mode === "product";

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 sm:p-6">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col shadow-xl overflow-hidden">

        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 flex items-start justify-between shrink-0 bg-white">
          <div className="flex items-center gap-4">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 border"
              style={{
                backgroundColor: THEME_PRIMARY_SOFT,
                borderColor: THEME_PRIMARY_RING,
                color: THEME_PRIMARY,
              }}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">
                Select Assets
              </h3>
              <p className="text-sm text-gray-500 mt-0.5">
                Choose one or more assets to link
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 -mr-2 rounded-full hover:bg-gray-100"
            aria-label="Close"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Sub-header / Search / Tabs */}
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/30 shrink-0">
          {campaignMode && (
            <div className="mb-3 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2">
              <svg
                className="w-4 h-4 text-blue-600 mt-0.5 shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-xs text-blue-800">
                Only assets{" "}
                <span className="font-semibold">mapped to a product</span> are
                shown here. To use any other asset, link it to a product first.
              </p>
            </div>
          )}

          {campaignMode && (
            <div
              role="tablist"
              aria-label="Asset selection mode"
              className="relative mb-4 grid grid-cols-2 rounded-xl bg-gray-200/60 p-1"
            >
              {/* Sliding highlight */}
              <span
                aria-hidden="true"
                className={`absolute inset-y-1 left-1 w-[calc(50%-4px)] rounded-lg bg-white shadow-sm border border-gray-200/50 transition-transform duration-200 ease-out ${mode === "product" ? "translate-x-full" : "translate-x-0"
                  }`}
              />
              {(
                [
                  {
                    key: "all",
                    label: "All assets",
                    icon: "🗂️",
                    count: assets.length,
                  },
                  {
                    key: "product",
                    label: "By product",
                    icon: "📦",
                    count: (productGroups || []).length,
                  },
                ] as const
              ).map((tab) => {
                const active = mode === tab.key;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setMode(tab.key)}
                    className={`relative z-10 flex items-center justify-center gap-2 rounded-lg py-2 text-sm font-medium transition-colors ${active
                      ? ""
                      : "text-gray-500 hover:text-gray-700"
                      }`}
                    style={active ? { color: THEME_PRIMARY } : undefined}
                  >
                    <span aria-hidden="true">{tab.icon}</span>
                    {tab.label}
                    <span
                      className={`min-w-[22px] rounded-full px-1.5 text-xs leading-5 transition-colors ${active
                        ? ""
                        : "bg-gray-200 text-gray-500"
                        }`}
                      style={
                        active
                          ? {
                            backgroundColor: THEME_PRIMARY_SOFT,
                            color: THEME_PRIMARY,
                          }
                          : undefined
                      }
                    >
                      {tab.count}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          <div className="relative">
            <input
              type="text"
              autoFocus
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={
                showProductView
                  ? "Search by product or asset name..."
                  : "Search assets..."
              }
              className="w-full pl-9 pr-8 py-2 border border-gray-300 rounded-lg text-sm shadow-sm focus:outline-none transition-shadow"
              onFocus={(e) => {
                e.currentTarget.style.boxShadow = `0 0 0 2px ${THEME_PRIMARY_RING}`;
                e.currentTarget.style.borderColor = THEME_PRIMARY;
              }}
              onBlur={(e) => {
                e.currentTarget.style.boxShadow = "";
                e.currentTarget.style.borderColor = "";
              }}
            />
            <svg
              className="absolute left-3 top-2.5 w-4 h-4 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                aria-label="Clear search"
              >
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="p-4 overflow-y-auto flex-1 bg-white">
          {showProductView ? (
            productGroupsLoading ? (
              <p className="text-sm text-gray-400 text-center py-8">
                Loading products...
              </p>
            ) : visibleGroups.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
                <p className="text-sm text-gray-500">
                  {searchQuery
                    ? "No products or assets match your search"
                    : "No products with mapped assets yet. Link assets to a product first."}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {visibleGroups.map((group) => {
                  const isOpen = !!term || openIds.includes(group.product_id);
                  const groupIds = group.assets.map((a) => a.id);
                  const selectedCount = groupIds.filter((id) =>
                    selectedIds.includes(id),
                  ).length;
                  const allSelected = selectedCount === groupIds.length;

                  return (
                    <div
                      key={group.product_id}
                      className="border border-gray-200 rounded-lg overflow-hidden transition-colors"
                    >
                      <div
                        className={`flex items-center gap-2 px-3 py-2.5 cursor-pointer select-none transition-colors ${isOpen ? "bg-gray-50 border-b border-gray-100" : "bg-white hover:bg-gray-50"
                          }`}
                        onClick={() => toggleGroup(group.product_id)}
                      >
                        <svg
                          className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? "rotate-90" : ""
                            }`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                        <span className="text-lg" aria-hidden="true">
                          📦
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-800 truncate">
                            {group.product_title}
                          </p>
                          <p className="text-xs text-gray-500">
                            {group.assets.length} asset
                            {group.assets.length === 1 ? "" : "s"}
                          </p>
                        </div>
                        {selectedCount > 0 && (
                          <span
                            className="px-2 py-0.5 text-xs font-medium rounded-full"
                            style={{ backgroundColor: THEME_PRIMARY_SOFT, color: THEME_PRIMARY }}
                          >
                            {selectedCount} selected
                          </span>
                        )}
                        {onSetMany && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onSetMany(groupIds, !allSelected);
                            }}
                            className="text-xs font-medium hover:underline whitespace-nowrap ml-2"
                            style={{ color: THEME_PRIMARY }}
                          >
                            {allSelected ? "Clear" : "Select all"}
                          </button>
                        )}
                      </div>

                      {isOpen && (
                        <div className="bg-white p-2 space-y-1">
                          {group.assets.map((a) =>
                            renderAssetRow(
                              a.id,
                              a.original_filename,
                              `${a.status || ""}${a.container ? ` • ${a.container}` : ""
                              }`,
                            ),
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )
          ) : assetsLoading ? (
            <p className="text-sm text-gray-400 text-center py-8">
              Loading assets...
            </p>
          ) : sorted.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <p className="text-sm text-gray-500 max-w-xs mx-auto">
                {searchQuery
                  ? "No assets match your search"
                  : campaignMode
                    ? "No assets available. Only assets linked to a product can be added to a campaign."
                    : "No assets available."}
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {sorted.map((asset) =>
                renderAssetRow(
                  asset.id,
                  asset.original_filename,
                  `${asset.status}${asset.container ? ` • ${asset.container}` : ""
                  }`,
                ),
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-white flex items-center justify-between shrink-0 rounded-b-2xl">
          <span className="text-sm font-medium text-gray-600">
            <span className="font-bold text-gray-900">{selectedIds.length}</span> selected
          </span>
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-white rounded-xl transition-opacity font-medium text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-1"
            style={{
              backgroundColor: THEME_PRIMARY,
              boxShadow: `0 0 0 0 ${THEME_PRIMARY_RING}`
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
          >
            Done
          </button>
        </div>

      </div>
    </div>
  );
};

interface EventPickerModalProps {
  events: Event[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  onClose: () => void;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
}

const EventPickerModal: React.FC<EventPickerModalProps> = ({
  events,
  selectedIds,
  onToggle,
  onClose,
  searchQuery,
  setSearchQuery,
}) => {
  const filtered = events.filter((e) =>
    e.eventName.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-xl max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Select Events</h2>
            <button
              onClick={onClose}
              className="text-black hover:text-black text-4xl font-bold leading-none"
            >
              ×
            </button>
          </div>

          <div className="relative mt-4">
            <input
              autoFocus
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search events..."
              className="w-full border rounded-lg px-3 py-2 pr-8"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {filtered.map((event) => (
            <label
              key={event.id}
              className="flex items-center gap-3 p-2 rounded hover:bg-gray-100 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selectedIds.includes(event.id)}
                onChange={() => onToggle(event.id)}
              />
              {event.eventName}
            </label>
          ))}
        </div>

        <div className="border-t p-4 flex justify-end">
          <button
            onClick={onClose}
            className="bg-purple-600 text-white px-5 py-2 rounded-lg"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

// ===== Product picker (used by the Create / Edit Event page) =====
interface PickerProduct {
  id: string;
  name: string;
  status?: string;
  details?: Product; // full product (when loaded) for the product page
}

interface ProductPickerModalProps {
  products: PickerProduct[];
  loading: boolean;
  selectedIds: string[];
  onToggle: (id: string) => void;
  onClose: () => void;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
}

const ProductPickerModal: React.FC<ProductPickerModalProps> = ({
  products,
  loading,
  selectedIds,
  onToggle,
  onClose,
  searchQuery,
  setSearchQuery,
}) => {
  const term = searchQuery.trim().toLowerCase();
  // Selected products float to the top so current picks are visible first
  const visible = products
    .filter((p) => (p.name || "").toLowerCase().includes(term))
    .sort(
      (a, b) =>
        (selectedIds.includes(a.id) ? 0 : 1) -
        (selectedIds.includes(b.id) ? 0 : 1),
    );

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 sm:p-6">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col shadow-xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 flex items-start justify-between shrink-0 bg-white">
          <div className="flex items-center gap-4">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 border"
              style={{
                backgroundColor: THEME_PRIMARY_SOFT,
                borderColor: THEME_PRIMARY_RING,
                color: THEME_PRIMARY,
              }}
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">Select Products</h3>
              <p className="text-sm text-gray-500 mt-0.5">
                Choose one or more products to link
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 -mr-2 rounded-full hover:bg-gray-100"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search */}
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/30 shrink-0">
          <div className="relative">
            <input
              type="text"
              autoFocus
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search products..."
              className="w-full pl-9 pr-8 py-2 border border-gray-300 rounded-lg text-sm shadow-sm focus:outline-none transition-shadow"
              onFocus={(e) => {
                e.currentTarget.style.boxShadow = `0 0 0 2px ${THEME_PRIMARY_RING}`;
                e.currentTarget.style.borderColor = THEME_PRIMARY;
              }}
              onBlur={(e) => {
                e.currentTarget.style.boxShadow = "";
                e.currentTarget.style.borderColor = "";
              }}
            />
            <svg className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                aria-label="Clear search"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="p-4 overflow-y-auto flex-1 bg-white">
          {loading && products.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">Loading products...</p>
          ) : visible.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <p className="text-sm text-gray-500 max-w-xs mx-auto">
                {searchQuery
                  ? "No products match your search"
                  : "No products yet. Create a product first."}
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {visible.map((p) => (
                <label
                  key={p.id}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer border border-transparent hover:bg-gray-50 hover:border-gray-200"
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(p.id)}
                    onChange={() => onToggle(p.id)}
                    className="rounded border-gray-300"
                    style={{ accentColor: THEME_PRIMARY }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {p.name || "Untitled product"}
                    </p>
                    {p.status && <p className="text-xs text-gray-400">{p.status}</p>}
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-white flex items-center justify-between shrink-0">
          <span className="text-sm font-medium text-gray-600">
            <span className="font-bold text-gray-900">{selectedIds.length}</span> selected
          </span>
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-white rounded-xl hover:opacity-90 transition-opacity font-medium text-sm shadow-sm"
            style={{ backgroundColor: THEME_PRIMARY }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

interface PaginationControlsProps {
  page: number;
  totalPages: number;
  total: number;
  pageSize: number;
  loading?: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

// Builds a compact page list like: 1, 2, 3, 4, 5, ..., 12
const buildPageList = (
  current: number,
  totalPages: number,
): (number | "...")[] => {
  const delta = 1;
  const range: (number | "...")[] = [];
  const start = Math.max(2, current - delta);
  const end = Math.min(totalPages - 1, current + delta);

  range.push(1);
  if (start > 2) range.push("...");
  for (let i = start; i <= end; i++) range.push(i);
  if (end < totalPages - 1) range.push("...");
  if (totalPages > 1) range.push(totalPages);

  return range;
};

const PaginationControls: React.FC<PaginationControlsProps> = ({
  page,
  totalPages,
  total,
  pageSize,
  loading,
  onPageChange,
  onPageSizeChange,
}) => {
  if (total === 0) return null;

  const pages = buildPageList(page, totalPages);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between px-2">
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <span>
          {loading
            ? "Loading…"
            : `Showing page ${page} of ${totalPages} (${total} total)`}
        </span>
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          className="ml-2 border border-gray-300 rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-purple-500"
          disabled={loading}
        >
          <option value={4}>4</option>
          <option value={8}>8</option>
          <option value={12}>12</option>
          <option value={24}>24</option>
        </select>
      </div>

      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1 || loading}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition"
        >
          ← Prev
        </button>

        {pages.map((p, idx) =>
          p === "..." ? (
            <span
              key={`ellipsis-${idx}`}
              className="px-2 text-gray-400 select-none"
            >
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onPageChange(p)}
              disabled={loading}
              className={`min-w-[36px] px-3 py-1.5 text-sm rounded-lg border transition ${p === page
                ? "bg-purple-600 text-white border-purple-600"
                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {p}
            </button>
          ),
        )}

        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages || loading}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition"
        >
          Next →
        </button>
      </div>
    </div>
  );
};

interface TagListProps {
  tags?: string[];
  limit?: number;
}

// Shows the first `limit` tags; the rest are hidden behind a "+N more" toggle.
const TagList: React.FC<TagListProps> = ({ tags = [], limit = 3 }) => {
  const [expanded, setExpanded] = useState(false);

  if (tags.length === 0) return null;

  const visibleTags = expanded ? tags : tags.slice(0, limit);
  const hiddenCount = tags.length - limit;

  return (
    <div className="mt-2 flex flex-wrap items-center gap-1">
      {visibleTags.map((tag) => (
        <span key={tag} className="text-xs text-gray-400">
          #{tag}
        </span>
      ))}
      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="text-xs font-medium text-purple-600 hover:text-purple-800"
        >
          {expanded ? "Show less" : `+${hiddenCount} more`}
        </button>
      )}
    </div>
  );
};

const CampaignsAndEvents: React.FC<CampaignsAndEventsProps> = ({
  tenant,
  view,
}) => {
  const loggedInUser = JSON.parse(localStorage.getItem("user") || "{}");
  const tenantId = loggedInUser.tenantId;
  const userId = loggedInUser.id;

  // ===== STATE =====
  // Campaigns
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(
    null,
  );
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");

  // ---- Server-side pagination: Campaigns ----
  const [campaignsPage, setCampaignsPage] = useState(1);
  const [campaignsPageSize, setCampaignsPageSize] = useState(8);
  const [campaignsTotal, setCampaignsTotal] = useState(0);
  const [campaignsTotalPages, setCampaignsTotalPages] = useState(1);
  const [campaignsLoading, setCampaignsLoading] = useState(false);

  // ---- Server-side pagination: Events ----
  const [eventsPage, setEventsPage] = useState(1);
  const [eventsPageSize, setEventsPageSize] = useState(8);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [eventsTotalPages, setEventsTotalPages] = useState(1);
  const [eventsLoading, setEventsLoading] = useState(false);

  // Debounce the search box (400ms) so we don't hit the API on every keystroke
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearchTerm(searchTerm), 400);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  // Products
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState<string | null>(null);
  const [productSearchInput, setProductSearchInput] = useState("");
  const debouncedProductSearch = useDebouncedValue(productSearchInput, 400);
  const [productStatusFilter, setProductStatusFilter] = useState("all");
  const [productPage, setProductPage] = useState(1);
  const [productLimit, setProductLimit] = useState(12);
  const [productTotal, setProductTotal] = useState(0);
  const [productTotalPages, setProductTotalPages] = useState(1);

  // Events
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  // Create / Edit Event opens as a full page (not a modal)
  const [showEventPage, setShowEventPage] = useState(false);
  const [eventMediaTab, setEventMediaTab] = useState<"image" | "video">("image");
  // "Open an Event" detail page (replaces the old Details modal)
  const [viewingEvent, setViewingEvent] = useState<Event | null>(null);
  const [detailLinks, setDetailLinks] = useState<{
    productIds?: string[];
    assetIds?: string[];
  }>({});
  const [detailLinksLoading, setDetailLinksLoading] = useState(false);

  // Drill-down inside an opened event: Event → Product → Asset (breadcrumb)
  const [viewingProduct, setViewingProduct] = useState<{ id: string; name: string } | null>(null);
  const [viewingAsset, setViewingAsset] = useState<{ id: string; name: string } | null>(null);
  // Set when an event is opened from a Product Details page: that product becomes
  // the first breadcrumb (instead of "Events") and links back to it
  const [eventOrigin, setEventOrigin] = useState<{
    name: string;
    path: string;
    state?: unknown;
  } | null>(null);
  // `undefined` = couldn't load (shows an error instead of "no assets")
  const [productPageAssets, setProductPageAssets] = useState<Asset[] | undefined>([]);
  const [productPageAssetsLoading, setProductPageAssetsLoading] = useState(false);
  const [assetDetail, setAssetDetail] = useState<Record<string, any> | null>(null);
  const [assetDetailLoading, setAssetDetailLoading] = useState(false);

  // Breadcrumb bar slot on the Library page (between the app header and "Library")
  const breadcrumbSlot = useLibraryBreadcrumbSlot();

  // Link products / assets on the event page
  const [pickerProducts, setPickerProducts] = useState<PickerProduct[]>([]);
  const [pickerProductsLoading, setPickerProductsLoading] = useState(false);
  const [eventLinksLoading, setEventLinksLoading] = useState(false);
  const [showEventProductPicker, setShowEventProductPicker] = useState(false);
  const [showEventAssetPicker, setShowEventAssetPicker] = useState(false);
  const [eventProductSearchQuery, setEventProductSearchQuery] = useState("");
  const [eventAssetSearchQuery, setEventAssetSearchQuery] = useState("");

  // Grid or list layout for the Events page (remembered across visits)
  const [eventsViewMode, setEventsViewMode] = useState<"grid" | "list">(() =>
    localStorage.getItem("eventsViewMode") === "list" ? "list" : "grid",
  );
  useEffect(() => {
    localStorage.setItem("eventsViewMode", eventsViewMode);
  }, [eventsViewMode]);

  // Archive visibility
  const [archiveVisibility, setArchiveVisibility] =
    useState<ArchiveVisibility>("Public");
  const [activeTab, setActiveTab] = useState<
    "campaigns" | "products" | "events"
  >("campaigns");

  // NEW: Navigation + Grid/List view mode
  const navigate = useNavigate();
  const location = useLocation();
  // Open a specific event when navigating from Product Details
  // Open a specific event when navigating from Product Details.
  // Avoid reopening the same event whenever the events array changes.
  useEffect(() => {
    const openEventId = location.state?.openEventId;

    if (!openEventId) return;

    const matchedEvent = events.find(
      (event) => String(event.id) === String(openEventId)
    );

    const passedEvent = location.state?.openEvent;

    const eventToOpen = matchedEvent || (
      passedEvent
        ? {
          ...passedEvent,
          id: String(passedEvent.id),
          eventName:
            passedEvent.eventName ||
            passedEvent.name ||
            passedEvent.title ||
            "Untitled Event",
        }
        : null
    );

    if (!eventToOpen) return;

    setActiveTab("events");
    setShowEventPage(false);
    setViewingProduct(null);
    setViewingAsset(null);

    setViewingEvent((previous) =>
      String(previous?.id) === String(eventToOpen.id)
        ? previous
        : eventToOpen as Event
    );

    // Came from a Product Details page? Show that product as the first breadcrumb
    setEventOrigin(location.state?.fromProduct ?? null);

    // Start at the top (header, breadcrumb, Library heading, event hero) instead of
    // keeping the scroll position the Product page had
    window.scrollTo({ top: 0, behavior: "auto" });

    // Open it only once: clear the hand-off so later list reloads
    // (search, paging) don't reopen it after going back to the list
    navigate(location.pathname + location.search, { replace: true, state: null });
  }, [location.state?.openEventId, events]);
  const [productViewMode, setProductViewMode] = useState<"grid" | "list">("grid");

  // When a fixed `view` is supplied (Customer Manager tabs), lock the internal tab to match it.
  useEffect(() => {
    if (view && view !== "summary") {
      setActiveTab(view as "campaigns" | "products" | "events");
    } else {
      // Check the URL for the tab parameter
      const searchParams = new URLSearchParams(window.location.search);
      const tabParam = searchParams.get("tab");

      if (tabParam === "products-only" || tabParam === "products") {
        setActiveTab("products");
      } else if (tabParam === "events-only" || tabParam === "events") {
        setActiveTab("events");
      } else if (tabParam === "campaigns-only" || tabParam === "campaigns") {
        setActiveTab("campaigns");
      } else {
        // Default fallback if no tab param is provided
        setActiveTab("campaigns");
      }
    }
  }, [view]);

  // Form states
  const [formData, setFormData] = useState<Partial<Campaign>>({
    title: "",
    description: "",
    startDate: getLocalToday(),
    endDate: "",
    notes: "",
    tags: [],
    status: "Active",
    image: "",
    video: "",
    eventIds: [], // NEW
    assetIds: [], // NEW
  });

  // Raw text of the product tags input (avoids join/split fighting the cursor)
  const [productTagsInput, setProductTagsInput] = useState("");

  const [productFormData, setProductFormData] = useState<Partial<Product>>({
    name: "",
    description: "",
    launchDate: "",
    endDate: "",
    notes: "",
    tags: [],
    status: "Active",
    image: "",
    video: "",
    assetIds: [], // NEW
  });

  // Raw text of the event tags input (so typing commas/backspacing isn't fought by join/split)
  const [eventTagsInput, setEventTagsInput] = useState("");

  const [eventFormData, setEventFormData] = useState<Partial<Event>>({
    eventName: "",
    description: "",
    eventDate: "",
    endDate: "",
    notes: "",
    tags: [],
    status: "Scheduled",
    image: "",
    video: "",
  });

  // UI helpers
  const [activeMediaTab, setActiveMediaTab] = useState<"image" | "video">(
    "image",
  );
  const [imagePreview, setImagePreview] = useState<string>("");
  const [videoPreview, setVideoPreview] = useState<string>("");
  const [newTag, setNewTag] = useState("");
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState<string>("");
  const [eventDropdownOpen, setEventDropdownOpen] = useState(false);
  const [eventSearchQuery, setEventSearchQuery] = useState("");
  const [assets, setAssets] = useState<Asset[]>([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [campaignAssets, setCampaignAssets] = useState<Asset[]>([]);
  const [campaignAssetsLoading, setCampaignAssetsLoading] = useState(false);
  const [productGroups, setProductGroups] = useState<ProductAssetGroup[]>([]);
  const [productGroupsLoading, setProductGroupsLoading] = useState(false);
  const [showAssetPicker, setShowAssetPicker] = useState(false);
  const [showEventPicker, setShowEventPicker] = useState(false);
  const [assetSearchQuery, setAssetSearchQuery] = useState("");
  const [showProductAssetPicker, setShowProductAssetPicker] = useState(false);
  const [productAssetSearchQuery, setProductAssetSearchQuery] = useState("");

  const singleViewHeading: Record<string, { title: string; subtitle: string }> =
  {
    campaigns: {
      title: "Campaigns",
      subtitle: "Create and manage your marketing campaigns",
    },
    events: {
      title: "Events",
      subtitle: "Create and manage your events",
    },
    products: {
      title: "Products",
      subtitle: "Create and manage your product launches",
    },
  };

  // Refs
  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const productImageInputRef = useRef<HTMLInputElement>(null);
  const eventImageInputRef = useRef<HTMLInputElement>(null);
  const eventVideoInputRef = useRef<HTMLInputElement>(null);

  // ===== FORMAT HELPERS =====
  const formatDate = (date: string) => {
    if (!date) return "-";
    const datePart = date.split("T")[0]; // read calendar date directly, no timezone shift
    const [year, month, day] = datePart.split("-");
    if (!year || !month || !day) return "-";
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return `${day} ${months[parseInt(month, 10) - 1]} ${year}`;
  };

  // ===== DATA LOADING =====

  const mapDemoAsset = (asset: any): Asset => ({
    id: String(asset.id),
    original_filename: asset.filename || "Untitled Asset",
    source_key: "",
    status: normalizeStatus(asset.status).toLowerCase() as Asset["status"],
    container:
      asset.filename?.split(".").pop()?.toLowerCase() || "",
    width: asset.width ?? null,
    height: asset.height ?? null,
    duration_sec: asset.durationSec ?? null,
    file_size_bytes: asset.sizeBytes ?? null,
    created_at: asset.uploadedAt || "",
    updated_at: asset.updatedAt || "",
    preview_url: asset.previewUrl || null,
    mime_type: asset.mimeType || null,
    kind: asset.kind || "",
  });

  // Campaigns – API (from feature/campaign-ame)
  const loadCampaigns = async (
    page: number = campaignsPage,
    limit: number = campaignsPageSize,
  ) => {
    if (!tenantId) return;
    setCampaignsLoading(true);
    try {
      const params = new URLSearchParams({
        tenant_id: tenantId,
        page: String(page),
        limit: String(limit),
      });
      if (filterStatus !== "all") params.append("status", filterStatus);
      if (debouncedSearchTerm) params.append("search", debouncedSearchTerm);

      const res = await fetch(
        `${API_V1_BASE_URL}/campaigns?${params.toString()}`,
      );
      const result = await res.json();
      if (result.success) {
        const mapped = result.data.map((c: any) => ({
          id: c.campaign_id,
          title: c.title,
          description: c.description,
          startDate: c.start_date,
          endDate: c.end_date,
          notes: c.notes,
          tags: c.tags,
          status: normalizeStatus(c.status) as Campaign["status"],
          image: c.image_url || "",
          video: c.video_url || "",
          eventIds: Array.isArray(c.event_ids) ? c.event_ids : [],
          assetIds: Array.isArray(c.asset_ids) ? c.asset_ids : [],
          createdBy: c.created_by,
          updatedBy: c.updated_by,
          createdAt: c.created_at,
          updatedAt: c.updated_at,
        }));
        setCampaigns(mapped);
        setCampaignsPage(result.page ?? page);
        setCampaignsTotal(result.total ?? mapped.length);
        setCampaignsTotalPages(
          result.totalPages ??
          Math.max(1, Math.ceil((result.total ?? mapped.length) / limit)),
        );
      }
    } catch (err) {
      console.error("Failed to load campaigns:", err);
    } finally {
      setCampaignsLoading(false);
    }
  };

  // Products – API (from HEAD)
  const loadProducts = async () => {
    setProductsLoading(true);
    setProductsError(null);


    try {
      const params = new URLSearchParams({
        page: String(productPage),
        limit: String(productLimit),
      });

      if (debouncedProductSearch) {
        params.append("search", debouncedProductSearch);
      }

      if (productStatusFilter !== "all") {
        params.append("status", productStatusFilter);
      }

      const response = await fetch(
        `${API_ROOT_URL}/products?${params.toString()}`,
        {
          method: "GET",
          headers: authenticatedJsonHeaders(),
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Failed to load products.");
      }

      const mappedProducts: Product[] = (result.data || []).map(
        (p: any) => mapProductFromApi(p)
      );

      setProducts(mappedProducts);

      setProductTotal(result.pagination?.total ?? mappedProducts.length);

      setProductTotalPages(
        result.pagination?.totalPages ??
        Math.max(1, Math.ceil(mappedProducts.length / productLimit))
      );
    } catch (error: any) {
      console.error("Failed to load products:", error);

      setProducts([]);
      setProductTotal(0);
      setProductTotalPages(1);
      setProductsError(error.message || "Could not load products.");
    } finally {
      setProductsLoading(false);
    }

  };

  const mapProductFromApi = (p: any): Product => ({
    id: p.product_id,
    name: p.title,
    description: p.description,
    launchDate: p.start_date,
    endDate: p.end_date,
    notes: p.notes,
    tags: Array.isArray(p.tags)
      ? p.tags
      : typeof p.tags === "string"
        ? p.tags
          .replace(/[{}"]/g, "")
          .split("•")
          .map((t: string) => t.trim())
          .filter(Boolean)
        : [],
    status: normalizeStatus(p.status) as Product["status"],
    image: p.image_url || "",
    video: p.video_url || "",
  });

  // >>> NEW: Event mapper (inserted right below mapProductFromApi)
  const mapEventFromApi = (e: any): Event => ({
    id: String(e.event_id ?? e.id),
    eventName: e.title || "Untitled Event",
    description: e.description || "",
    eventDate: e.start_date ? String(e.start_date).split("T")[0] : "",
    endDate: e.end_date ? String(e.end_date).split("T")[0] : "",
    notes: e.notes || "",
    tags: Array.isArray(e.tags)
      ? e.tags
      : typeof e.tags === "string"
        ? e.tags.replace(/[{}"]/g, "").split("•").map((t: string) => t.trim()).filter(Boolean)
        : [],
    status: normalizeStatus(e.status) as Event["status"],
    image: e.image_url || "",
    video: e.video_url || "",
  });

  // >>> NEW: Real asset rows (event-assets / assets / product-assets) -> Asset
  const mapApiAsset = (a: any): Asset => ({
    ...a,
    id: String(a.asset_id ?? a.id),
    original_filename: a.original_filename || "Untitled Asset",
    source_key: a.source_key || "",
    status: String(a.status || "").toLowerCase() as Asset["status"],
  });

  // >>> REPLACED: Events – API
  const loadEvents = async (
    page: number = eventsPage,
    limit: number = eventsPageSize,
  ) => {
    if (!tenantId) return;
    setEventsLoading(true);
    try {
      const params = new URLSearchParams({
        tenant_id: tenantId,
        page: String(page),
        limit: String(limit),
      });
      if (filterStatus !== "all") params.append("status", filterStatus);
      if (debouncedSearchTerm) params.append("search", debouncedSearchTerm);

      const res = await fetch(`${API_ORIGIN}/events?${params.toString()}`, {
        headers: authenticatedJsonHeaders(),
      });
      const result = await res.json();
      if (!res.ok || !result.success) {
        throw new Error(result.message || "Failed to load events");
      }

      const mapped: Event[] = (result.data || []).map(mapEventFromApi);
      setEvents(mapped);
      setEventsPage(result.page ?? page);
      setEventsTotal(result.total ?? mapped.length);
      setEventsTotalPages(
        result.totalPages ??
        Math.max(1, Math.ceil((result.total ?? mapped.length) / limit)),
      );
    } catch (error) {
      console.error("Failed to load events:", error);
      toast.error("Could not load events.");
    } finally {
      setEventsLoading(false);
    }
  };

  // >>> REPLACED: Assets – real API
  const loadAssets = async () => {
    setAssetsLoading(true);

    try {
      const res = await fetch(
        `${API_ROOT_URL}/assets?tenant_id=${tenantId}`,
        { headers: authenticatedJsonHeaders() },
      );
      const result = await res.json();
      if (!res.ok || result.success === false) {
        throw new Error(result.message || "Failed to load assets");
      }
      setAssets((result.data || []).map(mapApiAsset));
    } catch (error) {
      console.error("Failed to load assets:", error);
      setAssets([]);
    } finally {
      setAssetsLoading(false);
    }
  };

  // Products with their mapped assets, for the "By product" tab in the campaign picker
  const loadProductGroups = async () => {
    setProductGroupsLoading(true);

    try {
      const groups: ProductAssetGroup[] = simbDemoData.products.map(
        (product: any) => ({
          product_id: String(product.id),
          product_title: product.title || "Untitled Product",

          assets: simbDemoData.assets
            .filter((asset: any) =>
              (asset.productIds || []).some(
                (id: string) => String(id) === String(product.id)
              )
            )
            .map((asset: any) => {
              const mapped = mapDemoAsset(asset);

              return {
                id: mapped.id,
                original_filename: mapped.original_filename,
                status: mapped.status,
                container: mapped.container,
              };
            }),
        })
      );

      setProductGroups(groups);
    } catch (error) {
      console.error("Failed to load demo product groups:", error);
      setProductGroups([]);
    } finally {
      setProductGroupsLoading(false);
    }
  };

  const loadCampaignAssets = async () => {
    setCampaignAssetsLoading(true);

    try {
      await loadProductGroups();

      const mappedAssets: Asset[] = simbDemoData.assets
        .filter(
          (asset: any) =>
            Array.isArray(asset.productIds) &&
            asset.productIds.length > 0
        )
        .map((asset: any) => mapDemoAsset(asset));

      setCampaignAssets(mappedAssets);
    } catch (error) {
      console.error("Failed to load demo campaign assets:", error);
      setCampaignAssets([]);
    } finally {
      setCampaignAssetsLoading(false);
    }
  };

  // >>> REPLACED: All products (not just the current page) for the "Link products" picker
  const loadPickerProducts = async () => {
    setPickerProductsLoading(true);

    try {
      const res = await fetch(`${API_ROOT_URL}/products?tenant_id=${tenantId}`, {
        headers: authenticatedJsonHeaders(),
      });
      const result = await res.json();
      if (!res.ok || result.success === false) {
        throw new Error(result.message || "Failed to load products");
      }
      const fetched: PickerProduct[] = (result.data || []).map((p: any) => {
        const details = mapProductFromApi(p);
        return { id: details.id, name: details.name, status: details.status, details };
      });
      setPickerProducts(fetched);
    } catch (error) {
      console.error("Failed to load picker products:", error);
      setPickerProducts([]);
    } finally {
      setPickerProductsLoading(false);
    }
  };

  // >>> REPLACED: Linked products/assets of one event (for the Edit page).
  const fetchEventLinks = async (eventId: string) => {
    const get = async (path: string): Promise<any[] | undefined> => {
      try {
        const res = await fetch(`${EVENT_LINKS_BASE_URL}/${path}/${eventId}`, {
          headers: authenticatedJsonHeaders(),
        });
        const result = await res.json();
        return res.ok && result.success ? (result.data as any[]) : undefined;
      } catch (err) {
        console.error(`Failed to load ${path}`, err);
        return undefined;
      }
    };

    const [productRows, assetRows] = await Promise.all([
      get("event-products"),
      get("event-assets"),
    ]);

    // keep linked assets resolvable for chips / detail page names
    if (assetRows) {
      const mapped = assetRows.map(mapApiAsset);
      setAssets((prev) => {
        const known = new Set(prev.map((a) => a.id));
        return [...prev, ...mapped.filter((a) => !known.has(a.id))];
      });
    }

    return {
      productIds: productRows?.map((p) => String(p.product_id)),
      assetIds: assetRows?.map((a) => String(a.asset_id)),
    };
  };

  // Initial load — Products/Content/Assets aren't backend-paginated, so fetch once.
  // Campaigns/Events are fetched by the dedicated effects below (server-driven).
  useEffect(() => {
    loadAssets();
    loadCampaignAssets();
    loadEvents(1, eventsPageSize);
  }, []);

  // Campaigns: refetch from the backend whenever the tab is active, or the
  // page/page-size/status/search changes. Pagination, filtering, and search
  // all happen server + DB side — nothing is sliced/filtered on the client.
  useEffect(() => {
    if (activeTab !== "campaigns") return;

    loadCampaigns(campaignsPage, campaignsPageSize);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tenantId,
    activeTab,
    campaignsPage,
    campaignsPageSize,
    filterStatus,
    debouncedSearchTerm,
  ]);

  // Events: same pattern.
  useEffect(() => {
    if (activeTab !== "events") return;

    loadEvents(eventsPage, eventsPageSize);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tenantId,
    activeTab,
    eventsPage,
    eventsPageSize,
    filterStatus,
    debouncedSearchTerm,
  ]);

  // Reset to page 1 whenever the status filter or search text changes,
  // so the user isn't stranded on a page that no longer exists.
  useEffect(() => {
    setCampaignsPage(1);
    setEventsPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, debouncedSearchTerm]);

  // Product pagination effects
  useEffect(() => {
    setProductPage(1);
  }, [debouncedProductSearch, productStatusFilter, productLimit]);

  useEffect(() => {
    loadProducts();
  }, [
    tenantId,
    productPage,
    productLimit,
    debouncedProductSearch,
    productStatusFilter,
  ]);

  // "Open an Event": (re)load its linked products/assets whenever the detail
  // page is visible, including when coming back from Edit.
  useEffect(() => {
    if (!viewingEvent || showEventPage) return;

    let cancelled = false;

    const loadEventDetails = async () => {
      setDetailLinksLoading(true);

      try {
        // Load product names for the linked-product display.
        await loadPickerProducts();

        const links = await fetchEventLinks(viewingEvent.id);

        if (!cancelled) {
          setDetailLinks(links);
        }
      } catch (error) {
        console.error("Failed to load event details:", error);

        if (!cancelled) {
          setDetailLinks({
            productIds: [],
            assetIds: [],
          });
        }
      } finally {
        if (!cancelled) {
          setDetailLinksLoading(false);
        }
      }
    };

    void loadEventDetails();

    return () => {
      cancelled = true;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewingEvent?.id, showEventPage]);

  // >>> REPLACED: Product page assets effect (Event → Product)
  useEffect(() => {
    if (!viewingProduct) return;
    const productId = viewingProduct.id;
    let cancelled = false;

    (async () => {
      setProductPageAssetsLoading(true);
      setProductPageAssets([]);
      try {
        const res = await fetch(`${API_ROOT_URL}/product-assets/${productId}`, {
          headers: authenticatedJsonHeaders(),
        });
        const result = await res.json();
        if (!res.ok || result.success === false) throw new Error(result.message);
        if (!cancelled) setProductPageAssets((result.data || []).map(mapApiAsset));
      } catch (error) {
        console.error("Failed to load product assets:", error);
        if (!cancelled) setProductPageAssets(undefined); // shows the error message
      } finally {
        if (!cancelled) setProductPageAssetsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [viewingProduct?.id]);

  // Asset detail (Event → Asset)
  // There is no GET /assets/:id route on the backend, so read the record
  // (with its preview link) from the assets list and match it by id.
  useEffect(() => {
    if (!viewingAsset) return;
    const assetId = viewingAsset.id;
    let cancelled = false;

    (async () => {
      setAssetDetail(null);
      setAssetDetailLoading(true);
      try {
        const res = await fetch(
          `${API_ROOT_URL}/assets?tenant_id=${tenantId}&skip_preview=false`,
          { headers: authenticatedJsonHeaders() },
        );
        const result = await res.json().catch(() => null);
        if (!res.ok || !result) {
          throw new Error(result?.message || `Assets list failed (${res.status})`);
        }

        const rows: any[] = result.data ?? [];
        const match = rows.find(
          (r) => String(r.asset_id ?? r.id) === String(assetId),
        );

        if (!cancelled && match) {
          setAssetDetail({ ...match, id: String(match.asset_id ?? match.id) });
        } else if (!match) {
          console.warn("[asset detail] asset not found in list", assetId);
        }
      } catch (error) {
        console.error("Failed to load asset details:", error);
      } finally {
        if (!cancelled) setAssetDetailLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [viewingAsset?.id]);

  // ===== CAMPAIGN CRUD (API) =====
  const handleCreateCampaign = async () => {
    if (!formData.title) {
      toast.warning("Please fill in Title");
      return;
    }
    // Past dates are allowed for campaigns; only enforce end >= start.
    if (
      formData.startDate &&
      formData.endDate &&
      formData.endDate < formData.startDate
    ) {
      toast.warning("End date cannot be before the start date.");
      return;
    }
    if (!tenantId || !userId) {
      toast.error("Login session expired. Please login again.");
      return;
    }

    setSaving(true);
    setApiError("");

    try {
      const payload = {
        title: formData.title || "",
        description: formData.description || "",
        notes: formData.notes || "",
        start_date: formData.startDate
          ? new Date(formData.startDate).toISOString()
          : new Date().toISOString(),
        end_date: formData.endDate
          ? new Date(formData.endDate).toISOString()
          : null,
        created_by: userId,
        updated_by: userId,
        status: formData.status || "Active",
        tenant_id: tenantId,
        image_url: formData.image || "",
        video_url: formData.video || "",
        tags: formData.tags || [],
      };

      const res = await fetch(`${API_V1_BASE_URL}/campaigns/create`, {
        method: "POST",
        headers: authenticatedJsonHeaders(),
        body: JSON.stringify(payload),
      });

      const result = await res.json();
      if (!res.ok || !result.success) {
        throw new Error(result.message || "Failed to create campaign");
      }

      const created = result.data;

      // Link selected events to the newly created campaign (correct base path — no /v1)
      if (formData.eventIds && formData.eventIds.length > 0) {
        const linkEventsResponse = await fetch(
          `${API_ROOT_URL}/campaign-events`,
          {
            method: "POST",
            headers: authenticatedJsonHeaders(),
            body: JSON.stringify({
              campaign_id: created.campaign_id,
              event_ids: formData.eventIds,
              created_by: userId,
            }),
          },
        );
        const linkEventsResult = await linkEventsResponse.json();
        if (!linkEventsResponse.ok || !linkEventsResult.success) {
          throw new Error(
            linkEventsResult.message || "Failed to link campaign events",
          );
        }
      }

      // Link selected assets to the newly created campaign
      if (formData.assetIds && formData.assetIds.length > 0) {
        const linkAssetsResponse = await fetch(
          `${API_ROOT_URL}/campaign-assets`,
          {
            method: "POST",
            headers: authenticatedJsonHeaders(),
            body: JSON.stringify({
              campaign_id: created.campaign_id,
              asset_ids: formData.assetIds,
              created_by: userId,
            }),
          },
        );
        const linkAssetsResult = await linkAssetsResponse.json();
        if (!linkAssetsResponse.ok || !linkAssetsResult.success) {
          throw new Error(
            linkAssetsResult.message || "Failed to link campaign assets",
          );
        }
      }

      const newCampaign: Campaign = {
        id: created.campaign_id,
        title: created.title,
        description: created.description,
        startDate: created.start_date,
        endDate: created.end_date,
        notes: created.notes,
        tags: Array.isArray(created.tags) ? created.tags : [],
        status: created.status,
        image: created.image_url || "",
        video: created.video_url || "",
        eventIds: formData.eventIds || [],
        assetIds: formData.assetIds || [],
        createdBy: created.created_by,
        updatedBy: created.updated_by,
        createdAt: created.created_at,
        updatedAt: created.updated_at,
      };

      setCampaigns([newCampaign, ...campaigns]); // instant feedback
      setCampaignsTotal((t) => t + 1); // tab count updates immediately
      setCampaignsPage(1);
      loadCampaigns(1, campaignsPageSize); // sync order/page size with the server
      setShowCreateModal(false);
      resetForm();
      toast.success("Campaign created successfully!");
    } catch (error: any) {
      console.error(error);
      toast.error(error.message || "Failed to create campaign");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCampaign = async () => {
    if (!selectedCampaign) return;
    if (saving) return;
    // Past dates are allowed for campaigns; only enforce end >= start.
    if (
      formData.startDate &&
      formData.endDate &&
      formData.endDate < formData.startDate
    ) {
      toast.warning("End date cannot be before the start date.");
      return;
    }
    if (!tenantId || !userId) {
      toast.error("Login session expired. Please login again.");
      return;
    }
    if (!isValidUUID(selectedCampaign.id)) {
      toast.error("Campaign ID is invalid.");
      return;
    }

    setSaving(true);
    setApiError("");

    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        notes: formData.notes,
        start_date: formData.startDate
          ? new Date(formData.startDate).toISOString()
          : formData.startDate,
        end_date: formData.endDate
          ? new Date(formData.endDate).toISOString()
          : null,
        updated_by: userId,
        status: formData.status,
        tenant_id: tenantId,
        image_url: formData.image,
        video_url: formData.video,
        tags: formData.tags,
      };

      const res = await fetch(
        `${API_V1_BASE_URL}/campaigns/update/${selectedCampaign.id}`,
        {
          method: "PUT",
          headers: authenticatedJsonHeaders(),
          body: JSON.stringify(payload),
        },
      );

      const result = await res.json();
      if (!res.ok || !result.success) {
        throw new Error(result.message || "Update failed");
      }

      const updated = result.data;

      // Sync linked events — also when the list is now empty (unlink all)
      const eventsChanged =
        JSON.stringify([...(selectedCampaign.eventIds || [])].sort()) !==
        JSON.stringify([...(formData.eventIds || [])].sort());
      if (eventsChanged) {
        const linkEventsResponse = await fetch(
          `${API_ROOT_URL}/campaign-events/${selectedCampaign.id}`,
          {
            method: "PUT",
            headers: authenticatedJsonHeaders(),
            body: JSON.stringify({
              event_ids: formData.eventIds,
              updated_by: userId,
            }),
          },
        );
        const linkEventsResult = await linkEventsResponse.json();
        if (!linkEventsResponse.ok || !linkEventsResult.success) {
          throw new Error(
            linkEventsResult.message || "Failed to update campaign events",
          );
        }
      }

      // Sanitize so a stray undefined/null can never reach the API
      const cleanCampaignAssetIds = (formData.assetIds || []).filter(
        (id): id is string => typeof id === "string" && id.length > 0,
      );

      const assetsChanged =
        JSON.stringify([...(selectedCampaign.assetIds || [])].sort()) !==
        JSON.stringify([...cleanCampaignAssetIds].sort());
      if (assetsChanged) {
        const linkAssetsResponse = await fetch(
          `${API_ROOT_URL}/campaign-assets/${selectedCampaign.id}`,
          {
            method: "PUT",
            headers: authenticatedJsonHeaders(),
            body: JSON.stringify({
              asset_ids: cleanCampaignAssetIds,
              updated_by: userId,
            }),
          },
        );
        const linkAssetsResult = await linkAssetsResponse.json();
        if (!linkAssetsResponse.ok || !linkAssetsResult.success) {
          throw new Error(
            linkAssetsResult.message || "Failed to update campaign assets",
          );
        }
      }

      const updatedCampaign: Campaign = {
        id: updated.campaign_id,
        title: updated.title,
        description: updated.description,
        startDate: updated.start_date,
        endDate: updated.end_date,
        notes: updated.notes,
        tags: Array.isArray(updated.tags) ? updated.tags : [],
        status: normalizeStatus(updated.status) as Campaign["status"],
        image: updated.image_url || "",
        video: updated.video_url || "",
        eventIds: formData.eventIds || [],
        assetIds: cleanCampaignAssetIds,
        createdBy: updated.created_by,
        updatedBy: updated.updated_by,
        createdAt: updated.created_at,
        updatedAt: updated.updated_at,
      };

      setCampaigns((prev) =>
        prev.map((c) => (c.id === updatedCampaign.id ? updatedCampaign : c)),
      );
      setSelectedCampaign(null);
      setShowEditModal(false);
      resetForm();
      toast.success("Campaign updated successfully!");
    } catch (error: any) {
      console.error(error);
      toast.error(error.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCampaign = async (campaign: Campaign) => {
    const confirmed = await confirmToast(
      `Are you sure you want to delete "${campaign.title}"?`,
    );
    if (!confirmed) return;

    try {
      const res = await fetch(
        `${API_V1_BASE_URL}/campaigns/${campaign.id}?tenant_id=${tenantId}`,
        {
          method: "DELETE",
          headers: authenticatedJsonHeaders(),
        },
      );
      const result = await res.json();

      if (res.status === 409) {
        toast.warning(
          result.message ||
          "This campaign is linked to one or more events/assets and cannot be deleted. Unlink them first.",
        );
        return;
      }

      if (!res.ok || !result.success) {
        throw new Error(result.message || "Failed to delete campaign");
      }
      toast.success("Campaign deleted successfully!");
      setShowEditModal(false);
      setShowDetailsModal(false);
      setSelectedCampaign(null);
      setCampaigns((prev) => prev.filter((c) => c.id !== campaign.id));
      setCampaignsTotal((t) => Math.max(0, t - 1));
      if (campaigns.length === 1 && campaignsPage > 1) {
        setCampaignsPage(campaignsPage - 1); // the page effect refetches
      } else {
        await loadCampaigns(campaignsPage, campaignsPageSize);
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to delete campaign");
    }
  };

  // ===== PRODUCT CRUD (API from HEAD) =====
  const mapProductFormToApiPayload = (form: Partial<Product>) => ({
    title: form.name,
    description: form.description || "",
    notes: form.notes || "",
    start_date: form.launchDate
      ? new Date(form.launchDate).toISOString()
      : new Date().toISOString(),
    end_date: form.endDate ? new Date(form.endDate).toISOString() : null,
    created_by: userId,
    updated_by: userId,
    status: form.status || "Active",
    tenant_id: tenantId,
    tags: form.tags || [],
  });

  const handleCreateProduct = async () => {
    if (saving) return; // prevent double submit
    if (!productFormData.name) {
      toast.warning("Please enter product name");
      return;
    }
    // Past dates are allowed for products; only enforce end >= launch.
    if (
      productFormData.launchDate &&
      productFormData.endDate &&
      productFormData.endDate < productFormData.launchDate
    ) {
      toast.warning("End date cannot be before the launch date.");
      return;
    }
    setSaving(true);
    try {
      const payload = mapProductFormToApiPayload(productFormData);
      const response = await fetch(`${API_ROOT_URL}/products`, {
        method: "POST",
        headers: authenticatedJsonHeaders(),
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || "Failed to create product");
      }
      const result = await response.json();
      const created = result.data ?? result;

      const newProductId = created.product_id ?? created.id;
      const cleanAssetIds = (productFormData.assetIds || []).filter(
        (id): id is string => typeof id === "string" && id.length > 0,
      );
      if (newProductId && cleanAssetIds.length > 0) {
        const linkRes = await fetch(
          `${API_ROOT_URL}/product-assets/${newProductId}`,
          {
            method: "PUT",
            headers: authenticatedJsonHeaders(),
            body: JSON.stringify({
              asset_ids: cleanAssetIds,
              updated_by: userId,
            }),
          },
        );
        if (!linkRes.ok) {
          toast.warning("Product created, but linking assets failed.");
        }
      }

      const newProduct: Product = {
        id: created.product_id ?? created.id,
        name: created.title,
        description: created.description,
        launchDate: created.start_date ? created.start_date.split("T")[0] : "",
        endDate: created.end_date ? created.end_date.split("T")[0] : "",
        notes: created.notes,
        tags: Array.isArray(created.tags)
          ? created.tags
          : typeof created.tags === "string"
            ? created.tags
              .replace(/[{}"]/g, "")
              .split("•")
              .map((t: string) => t.trim())
              .filter(Boolean)
            : [],
        status: created.status,
        image: created.image_url || productFormData.image || "",
        video: created.video_url || productFormData.video || "",
      };
      setProducts([newProduct, ...products]); // instant feedback
      setProductTotal((t) => t + 1); // count updates immediately
      if (productPage === 1) {
        loadProducts(); // already on page 1, so refetch directly
      } else {
        setProductPage(1); // the product effect refetches
      }
      setShowProductModal(false);
      resetProductForm();
      toast.success("Product created successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Error creating product. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const productFieldMap: Record<string, string> = {
    name: "title",
    description: "description",
    notes: "notes",
    launchDate: "start_date",
    endDate: "end_date",
    status: "status",
    tags: "tags",
  };

  const buildProductDiffPayload = (
    original: Product,
    updated: Partial<Product>,
  ): Record<string, any> => {
    const diff: Record<string, any> = {};
    Object.entries(productFieldMap).forEach(([uiField, apiField]) => {
      const originalValue = (original as any)[uiField];
      const updatedValue = (updated as any)[uiField];
      if (JSON.stringify(originalValue) !== JSON.stringify(updatedValue)) {
        if (uiField === "launchDate" || uiField === "endDate") {
          diff[apiField] = updatedValue
            ? new Date(updatedValue).toISOString()
            : null;
        } else {
          diff[apiField] = updatedValue;
        }
      }
    });
    return diff;
  };

  const handleUpdateProduct = async () => {
    if (!selectedProduct) return;
    if (saving) return; // prevent double submit
    // Past dates are allowed for products; only enforce end >= launch.
    if (
      productFormData.launchDate &&
      productFormData.endDate &&
      productFormData.endDate < productFormData.launchDate
    ) {
      toast.warning("End date cannot be before the launch date.");
      return;
    }
    setSaving(true);
    try {
      const cleanAssetIds = (productFormData.assetIds || []).filter(
        (id): id is string => typeof id === "string" && id.length > 0,
      );
      const changedFields = buildProductDiffPayload(
        selectedProduct,
        productFormData,
      );
      const assetsChanged =
        JSON.stringify([...(selectedProduct.assetIds || [])].sort()) !==
        JSON.stringify([...cleanAssetIds].sort());

      if (Object.keys(changedFields).length === 0 && !assetsChanged) {
        toast.info("No changes to save");
        setSaving(false);
        return;
      }

      let updated: any = selectedProduct;

      if (Object.keys(changedFields).length > 0) {
        const payload = {
          ...changedFields,
          updated_by: userId,
          tenant_id: tenantId,
        };
        const response = await fetch(
          `${API_ROOT_URL}/products/${selectedProduct.id}`,
          {
            method: "PUT",
            headers: authenticatedJsonHeaders(),
            body: JSON.stringify(payload),
          },
        );
        if (!response.ok) {
          const errText = await response.text();
          throw new Error(errText || "Failed to update product");
        }
        updated = await response.json();
      }

      // Sync linked assets independently of other field changes
      if (assetsChanged) {
        const linkRes = await fetch(
          `${API_ROOT_URL}/product-assets/${selectedProduct.id}`,
          {
            method: "PUT",
            headers: authenticatedJsonHeaders(),
            body: JSON.stringify({
              asset_ids: cleanAssetIds,
              updated_by: userId,
            }),
          },
        );
        const linkResult = await linkRes.json().catch(() => ({}));
        if (!linkRes.ok || linkResult.success === false) {
          throw new Error(
            linkResult.message || "Failed to link assets to product",
          );
        }
      }

      setProducts(
        products.map((p) =>
          p.id === selectedProduct.id
            ? {
              ...p,
              name: updated.title ?? p.name,
              description: updated.description ?? p.description,
              notes: updated.notes ?? p.notes,
              launchDate: updated.start_date ?? p.launchDate,
              endDate: updated.end_date ?? p.endDate,
              status: updated.status ?? p.status,
              tags: updated.tags ?? p.tags,
              assetIds: cleanAssetIds,
            }
            : p,
        ),
      );
      setShowProductModal(false);
      setSelectedProduct(null);
      resetProductForm();
      toast.success("Product updated successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Error updating product. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProduct = async (product: Product) => {
    const confirmed = await confirmToast(
      `Are you sure you want to delete "${product.name}"?`,
    );
    if (!confirmed) return;

    try {
      const res = await fetch(
        `${API_ROOT_URL}/products/${product.id}?tenant_id=${tenantId}`,
        {
          method: "DELETE",
          headers: authenticatedJsonHeaders(),
        },
      );
      const result = await res.json();

      if (res.status === 409) {
        toast.warning(
          result.message ||
          "This product is linked to one or more assets and cannot be deleted.",
        );
        return;
      }

      if (!res.ok || !result.success) {
        throw new Error(result.message || "Failed to delete product");
      }

      toast.success("Product deleted successfully!");
      setShowProductModal(false);
      setSelectedProduct(null);
      setProducts((prev) => prev.filter((p) => p.id !== product.id));
      setProductTotal((t) => Math.max(0, t - 1));
      if (products.length === 1 && productPage > 1) {
        setProductPage(productPage - 1); // the page effect refetches
      } else {
        await loadProducts();
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to delete product");
    }
  };

  // ===== EVENT CRUD (API from HEAD) =====
  const mapEventFormToApiPayload = (form: Partial<Event>) => ({
    title: form.eventName,
    description: form.description || "",
    notes: form.notes || "",
    start_date: form.eventDate
      ? new Date(form.eventDate).toISOString()
      : new Date().toISOString(),
    end_date: form.endDate ? new Date(form.endDate).toISOString() : null,
    created_by: userId,
    updated_by: userId,
    status: form.status || "Scheduled",
    tenant_id: tenantId,
    tags: form.tags || [],
    image_url: form.image || "",
    video_url: form.video || "",
  });

  // ----- Event ↔ product / asset links -----
  const sameIdSet = (a: string[], b: string[]) =>
    JSON.stringify([...a].sort()) === JSON.stringify([...b].sort());

  // `before` is undefined when existing links couldn't be loaded:
  // then only save if the user actually picked something.
  const linkNeedsSave = (ids: string[], before?: string[]) =>
    before === undefined ? ids.length > 0 : !sameIdSet(ids, before);

  // Create mode (no `original`): POST each non-empty list.
  // Update mode: PUT the full list for each type that changed (replaces the set).
  // Returns one readable reason per failed list, e.g.
  // "products: the /event-products endpoint doesn't exist on the server yet (404)".
  const saveEventLinks = async (
    eventId: string,
    links: { productIds: string[]; assetIds: string[] },
    original?: { productIds?: string[]; assetIds?: string[] },
  ): Promise<string[]> => {
    const targets = [
      { key: "productIds", path: "event-products", field: "product_ids", label: "products" },
      { key: "assetIds", path: "event-assets", field: "asset_ids", label: "assets" },
    ] as const;

    const failed: string[] = [];
    for (const t of targets) {
      const ids = links[t.key];
      const shouldSave = original
        ? linkNeedsSave(ids, original[t.key])
        : ids.length > 0;
      if (!shouldSave) continue;

      const method = original ? "PUT" : "POST";
      const url = original
        ? `${EVENT_LINKS_BASE_URL}/${t.path}/${eventId}`
        : `${EVENT_LINKS_BASE_URL}/${t.path}`;

      try {
        const res = await fetch(url, {
          method,
          headers: authenticatedJsonHeaders(),
          body: JSON.stringify(
            original
              ? { [t.field]: ids, updated_by: userId }
              : { event_id: eventId, [t.field]: ids, created_by: userId },
          ),
        });
        const result = await res.json().catch(() => ({}));
        if (res.ok && result.success !== false) continue;

        console.error(`[event links] ${method} ${url} → ${res.status}`, result);
        // Express's default 404 is an HTML page (no JSON message) = route missing.
        const reason =
          res.status === 404 && !result.message
            ? `the /${t.path} endpoint doesn't exist on the server yet (404)`
            : result.message || `server returned ${res.status}`;
        failed.push(`${t.label}: ${reason}`);
      } catch (err) {
        console.error(`[event links] ${method} ${url} failed`, err);
        failed.push(`${t.label}: could not reach the server`);
      }
    }
    return failed;
  };

  const handleCreateEvent = async () => {
    if (saving) return; // prevent double submit

    if (!eventFormData.eventName?.trim()) {
      toast.warning("Please enter event name");
      return;
    }

    if (
      eventFormData.eventDate &&
      eventFormData.endDate &&
      eventFormData.endDate < eventFormData.eventDate
    ) {
      toast.warning("End date cannot be before the event date.");
      return;
    }

    setSaving(true);
    try {
      const payload = mapEventFormToApiPayload(eventFormData);
      
      // Ensure the backend receives EXACTLY what is in the DB ("INACTIVE")
      const finalPayload: any = { ...payload };
      if (finalPayload.status === "Inactive" || finalPayload.status === "INACTIVE") {
        finalPayload.status = "INACTIVE";
      }

      const response = await fetch(`${API_ORIGIN}/events`, {
        method: "POST",
        headers: authenticatedJsonHeaders(),
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || "Failed to create event");
      }
      const result = await response.json();
      const created = result.data ?? result;

      // Link the picked products/assets. A failure here doesn't undo the event;
      // the user gets a warning and can link them again from Edit.
      const newEventId = created.event_id ?? created.id;
      const linkFailures = newEventId
        ? await saveEventLinks(String(newEventId), {
          productIds: eventFormData.productIds || [],
          assetIds: eventFormData.assetIds || [],
        })
        : [];

      const newEvent: Event = {
        id: created.event_id ?? created.id,
        eventName: created.title,
        description: created.description,
        eventDate: created.start_date ? created.start_date.split("T")[0] : "",
        endDate: created.end_date ? created.end_date.split("T")[0] : "",
        notes: created.notes,
        tags: Array.isArray(created.tags)
          ? created.tags
          : typeof created.tags === "string"
            ? created.tags
              .replace(/[{}"]/g, "")
              .split("•")
              .map((t: string) => t.trim())
              .filter(Boolean)
            : [],
        status: created.status,
        image: eventFormData.image || "",
        video: eventFormData.video || "",
      };
      setEvents([newEvent, ...events]); // instant feedback
      setEventsTotal((t) => t + 1); // tab count updates immediately
      setEventsPage(1);
      loadEvents(1, eventsPageSize); // sync with the server
      setShowEventPage(false);
      resetEventForm();
      toast.success("Event created successfully!");
      if (linkFailures.length > 0) {
        toast.warning(
          `Event created, but its links weren't saved — ${linkFailures.join("; ")}`,
          { autoClose: 8000 },
        );
      }
    } catch (error) {
      console.error(error);
      toast.error("Error creating event. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const eventFieldMap: Record<string, string> = {
    eventName: "title",
    description: "description",
    notes: "notes",
    eventDate: "start_date",
    endDate: "end_date",
    status: "status",
    tags: "tags",
    image: "image_url",
    video: "video_url",
  };

  const buildEventDiffPayload = (
    original: Event,
    updated: Partial<Event>,
  ): Record<string, any> => {
    const diff: Record<string, any> = {};
    Object.entries(eventFieldMap).forEach(([uiField, apiField]) => {
      const originalValue = (original as any)[uiField];
      const updatedValue = (updated as any)[uiField];
      if (JSON.stringify(originalValue) !== JSON.stringify(updatedValue)) {
        if (uiField === "eventDate" || uiField === "endDate") {
          diff[apiField] = updatedValue
            ? new Date(updatedValue).toISOString()
            : null;
        } else {
          diff[apiField] = updatedValue;
        }
      }
    });
    return diff;
  };

  const handleUpdateEvent = async () => {
    if (!selectedEvent) return;
    if (saving) return; // prevent double submit
    // Past dates are allowed for events; only enforce end >= start.
    if (
      eventFormData.eventDate &&
      eventFormData.endDate &&
      eventFormData.endDate < eventFormData.eventDate
    ) {
      toast.warning("End date cannot be before the event date.");
      return;
    }
    setSaving(true);
    try {
      const changedFields = buildEventDiffPayload(selectedEvent, eventFormData);
      const links = {
        productIds: eventFormData.productIds || [],
        assetIds: eventFormData.assetIds || [],
      };
      const original = {
        productIds: selectedEvent.productIds,
        assetIds: selectedEvent.assetIds,
      };
      const linksChanged =
        linkNeedsSave(links.productIds, original.productIds) ||
        linkNeedsSave(links.assetIds, original.assetIds);

      if (Object.keys(changedFields).length === 0 && !linksChanged) {
        toast.info("No changes to save");
        return; // `finally` resets saving
      }

      // 1) Event fields (only if something changed)
      let updated: any = {};
      if (Object.keys(changedFields).length > 0) {
        
        // Ensure the backend receives EXACTLY what is in the DB ("INACTIVE")
        const finalPayload: any = {
          ...changedFields,
          updated_by: userId,
          tenant_id: tenantId,
        };

        if (finalPayload.status === "Inactive" || finalPayload.status === "INACTIVE") {
          finalPayload.status = "INACTIVE";
        }

        const response = await fetch(`${API_ORIGIN}/events/${selectedEvent.id}`, {
          method: "PUT",
          headers: authenticatedJsonHeaders(),
          body: JSON.stringify(finalPayload),
        });
        if (!response.ok) {
          const errText = await response.text();
          throw new Error(errText || "Failed to update event");
        }
        const result = await response.json();
        updated = result.data ?? result;
      }

      // 2) Linked products / assets (only the lists that changed)
      const linkFailures = linksChanged
        ? await saveEventLinks(selectedEvent.id, links, original)
        : [];

      setEvents((prev) =>
        prev.map((ev) =>
          ev.id === selectedEvent.id
            ? {
              ...ev,
              eventName: updated.title ?? ev.eventName,
              description: updated.description ?? ev.description,
              notes: updated.notes ?? ev.notes,
              eventDate: updated.start_date
                ? updated.start_date.split("T")[0]
                : ev.eventDate,
              endDate: updated.end_date
                ? updated.end_date.split("T")[0]
                : ev.endDate,
              status: updated.status ?? ev.status,
              tags: updated.tags ?? ev.tags,
              image: updated.image_url ?? eventFormData.image ?? ev.image,
              video: updated.video_url ?? eventFormData.video ?? ev.video,
            }
            : ev,
        ),
      );
      setShowEventPage(false);
      setSelectedEvent(null);
      resetEventForm();
      toast.success("Event updated successfully!");
      if (linkFailures.length > 0) {
        toast.warning(
          `Event saved, but its links weren't updated — ${linkFailures.join("; ")}`,
          { autoClose: 8000 },
        );
      }
    } catch (error) {
      console.error(error);
      toast.error("Error updating event. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEvent = async (event: Event) => {
    const confirmed = await confirmToast(
      `Delete "${event.eventName}"? It will be hidden from Events. Its linked products and assets are kept.`,
    );
    if (!confirmed) return;

    try {
      const res = await fetch(
        `${API_ORIGIN}/events/${event.id}?tenant_id=${tenantId}`,
        {
          method: "DELETE",
          headers: authenticatedJsonHeaders(),
        },
      );
      const result = await res.json();

      if (res.status === 409) {
        toast.warning(
          result.message ||
          "This event is linked to a campaign and cannot be deleted.",
        );
        return;
      }

      if (!res.ok || !result.success) {
        throw new Error(result.message || "Failed to delete event");
      }

      toast.success("Event deleted successfully!");
      setShowEventPage(false);
      setViewingEvent((v) => (v?.id === event.id ? null : v)); // leave its detail page
      setSelectedEvent(null);
      setEvents((prev) => prev.filter((e) => e.id !== event.id));
      setEventsTotal((t) => Math.max(0, t - 1));
      if (events.length === 1 && eventsPage > 1) {
        setEventsPage(eventsPage - 1); // the page effect refetches
      } else {
        await loadEvents(eventsPage, eventsPageSize);
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to delete event");
    }
  };

  // ===== RESET FORMS =====
  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      startDate: getLocalToday(),
      endDate: "",
      notes: "",
      tags: [],
      status: "Active",
      image: "",
      video: "",
      eventIds: [], // NEW
      assetIds: [], // NEW
    });
    setImagePreview("");
    setVideoPreview("");
    setNewTag("");
    setActiveMediaTab("image");
  };

  const resetProductForm = () => {
    setProductTagsInput("");
    setProductFormData({
      name: "",
      description: "",
      launchDate: "",
      endDate: "",
      notes: "",
      tags: [],
      status: "Active",
      image: "",
      video: "",
      assetIds: [], // NEW
    });
  };

  const resetEventForm = () => {
    setEventTagsInput("");
    setEventFormData({
      eventName: "",
      description: "",
      eventDate: getLocalToday(),
      endDate: "",
      notes: "",
      tags: [],
      status: "Scheduled",
      image: "",
      video: "",
      productIds: [],
      assetIds: [],
    });
  };

  const findAssetById = (id: string) =>
    campaignAssets.find((a) => a.id === id) ?? assets.find((a) => a.id === id);

  // ===== TAG HANDLING =====
  const addTag = () => {
    // Accept comma-separated input: "tag1,tag2,tag3" becomes 3 tags
    const incoming = newTag
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    if (incoming.length === 0) return;

    setFormData((prev) => {
      const existing = prev.tags || [];
      const merged = [...existing];
      incoming.forEach((t) => {
        if (!merged.includes(t)) merged.push(t); // skip duplicates, also within the same input
      });
      return { ...prev, tags: merged };
    });
    setNewTag("");
  };
  const removeTag = (tag: string) => {
    setFormData({
      ...formData,
      tags: formData.tags?.filter((t) => t !== tag) || [],
    });
  };

  const toggleEventSelection = (eventId: string) => {
    setFormData((prev) => {
      const current = prev.eventIds || [];
      const updated = current.includes(eventId)
        ? current.filter((id) => id !== eventId)
        : [...current, eventId];
      return { ...prev, eventIds: updated };
    });
  };

  const removeSelectedEvent = (eventId: string) => {
    setFormData((prev) => ({
      ...prev,
      eventIds: (prev.eventIds || []).filter((id) => id !== eventId),
    }));
  };

  const toggleAssetSelection = (assetId: string) => {
    setFormData((prev) => {
      const current = prev.assetIds || [];
      const updated = current.includes(assetId)
        ? current.filter((id) => id !== assetId)
        : [...current, assetId];
      return { ...prev, assetIds: updated };
    });
  };

  const removeSelectedAsset = (assetId: string) => {
    setFormData((prev) => ({
      ...prev,
      assetIds: (prev.assetIds || []).filter((id) => id !== assetId),
    }));
  };

  // Select / clear many assets at once (used by "Select all" in the By product tab)
  const setAssetsSelection = (ids: string[], select: boolean) => {
    setFormData((prev) => {
      const current = prev.assetIds || [];
      const updated = select
        ? Array.from(new Set([...current, ...ids]))
        : current.filter((id) => !ids.includes(id));
      return { ...prev, assetIds: updated };
    });
  };

  const toggleProductAssetSelection = (assetId: string) => {
    setProductFormData((prev) => {
      const current = prev.assetIds || [];
      const updated = current.includes(assetId)
        ? current.filter((id) => id !== assetId)
        : [...current, assetId];
      return { ...prev, assetIds: updated };
    });
  };

  const removeSelectedProductAsset = (assetId: string) => {
    setProductFormData((prev) => ({
      ...prev,
      assetIds: (prev.assetIds || []).filter((id) => id !== assetId),
    }));
  };

  const addProductTag = () => {
    if (newTag && !productFormData.tags?.includes(newTag)) {
      setProductFormData({
        ...productFormData,
        tags: [...(productFormData.tags || []), newTag],
      });
      setNewTag("");
    }
  };
  const removeProductTag = (tag: string) => {
    setProductFormData({
      ...productFormData,
      tags: productFormData.tags?.filter((t) => t !== tag) || [],
    });
  };

  const addEventTag = () => {
    // "tag1, tag2" becomes 2 tags; duplicates are skipped
    const incoming = newTag
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    if (incoming.length === 0) return;

    setEventFormData((prev) => {
      const merged = [...(prev.tags || [])];
      incoming.forEach((t) => {
        if (!merged.includes(t)) merged.push(t);
      });
      return { ...prev, tags: merged };
    });
    setNewTag("");
  };
  const removeEventTag = (tag: string) => {
    setEventFormData({
      ...eventFormData,
      tags: eventFormData.tags?.filter((t) => t !== tag) || [],
    });
  };

  // Add or remove one product/asset on the event form (also used by the chip ×)
  const toggleEventLink = (key: "productIds" | "assetIds", id: string) => {
    setEventFormData((prev) => {
      const current = prev[key] || [];
      const next = current.includes(id)
        ? current.filter((x) => x !== id)
        : [...current, id];
      return key === "productIds"
        ? { ...prev, productIds: next }
        : { ...prev, assetIds: next };
    });
  };

  // ===== UI HELPERS =====
  const getStatusColor = (status: string) => {
    return status === "Active"
      ? "bg-green-100 text-green-700"
      : status === "Scheduled"
        ? "bg-yellow-100 text-yellow-700"
        : "bg-gray-100 text-gray-700";
  };
  const getStatusIcon = (status: string) => {
    return status === "Active" ? "✅" : "📦";
  };

  // ===== FILTERS =====
  const getFilteredCampaigns = () => {
    // Status filter, search, and pagination now happen server-side
    // (see loadCampaigns) — `campaigns` is already the current page's data.
    return campaigns;
  };

  const getFilteredEvents = () => {
    // Status filter, search, and pagination now happen server-side
    // (see loadEvents) — `events` is already the current page's data.
    return events;
  };

  // ===== ARCHIVE COUNTS =====
  const archiveCounts: ArchiveCounts = {
    campaigns: campaigns.filter((c) => c.status === "Archived").length,
    products: products.filter((p) => p.status === "Archived").length,
    events: events.filter((e) => e.status === "Archived").length,
    total:
      campaigns.filter((c) => c.status === "Archived").length +
      products.filter((p) => p.status === "Archived").length +
      events.filter((e) => e.status === "Archived").length,
  };

  const filteredCampaigns = getFilteredCampaigns();
  const filteredProducts = products;
  const filteredEvents = getFilteredEvents();

  // ===== MEDIA HANDLING =====
  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.warning("Image size should be less than 5MB");
        return;
      }
      if (!file.type.match("image.*")) {
        toast.warning("Please upload an image file");
        return;
      }
      const base64 = await convertToBase64(file);
      setImagePreview(base64);
      setFormData((prev) => ({ ...prev, image: base64, video: "" }));
      setActiveMediaTab("image");
    }
  };

  const handleVideoUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 20 * 1024 * 1024) {
        toast.warning("Video size should be less than 20MB");
        return;
      }
      if (!file.type.match("video.*")) {
        toast.warning("Please upload a video file");
        return;
      }
      const base64 = await convertToBase64(file);
      setVideoPreview(base64);
      setFormData((prev) => ({ ...prev, video: base64, image: "" }));
      setActiveMediaTab("video");
    }
  };

  // Remove the uploaded poster/video so the user can pick a new file.
  // Resetting the input's value is required, otherwise re-selecting the
  // same file would not fire onChange.
  const removeCampaignImage = () => {
    setImagePreview("");
    setFormData((prev) => ({ ...prev, image: "" }));
    if (imageInputRef.current) imageInputRef.current.value = "";
  };

  const removeCampaignVideo = () => {
    setVideoPreview("");
    setFormData((prev) => ({ ...prev, video: "" }));
    if (videoInputRef.current) videoInputRef.current.value = "";
  };

  // Event media: image OR video, same size/type rules as campaign media
  const handleEventMediaFile = async (
    file: File | undefined,
    kind: "image" | "video",
  ) => {
    if (!file) return;
    const limitMb = kind === "image" ? 5 : 20;
    if (file.size > limitMb * 1024 * 1024) {
      toast.warning(
        `${kind === "image" ? "Image" : "Video"} size should be less than ${limitMb}MB`,
      );
      return;
    }
    if (!file.type.startsWith(`${kind}/`)) {
      toast.warning(
        `Please upload ${kind === "image" ? "an image" : "a video"} file`,
      );
      return;
    }
    const base64 = await convertToBase64(file);
    setEventFormData((prev) =>
      kind === "image"
        ? { ...prev, image: base64, video: "" }
        : { ...prev, video: base64, image: "" },
    );
    setEventMediaTab(kind);
  };

  const removeEventMedia = (kind: "image" | "video") => {
    setEventFormData((prev) =>
      kind === "image" ? { ...prev, image: "" } : { ...prev, video: "" },
    );
    const ref = kind === "image" ? eventImageInputRef : eventVideoInputRef;
    if (ref.current) ref.current.value = "";
  };

  // ===== RENDER CARDS =====
  const renderCampaignCard = (campaign: Campaign) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300 group">
      <div className="relative h-48 overflow-hidden bg-gray-100">
        {campaign.image ? (
          <img
            src={campaign.image}
            alt={campaign.title}
            className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400 bg-gradient-to-br from-gray-100 to-gray-200">
            <span className="text-4xl">🎯</span>
          </div>
        )}
        <div className="absolute top-2 right-2">
          <span
            className={`px-2 py-1 text-xs rounded-full ${getStatusColor(campaign.status)} flex items-center gap-1`}
          >
            {getStatusIcon(campaign.status)} {campaign.status}
          </span>
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-gray-800 truncate text-lg">
          {campaign.title}
        </h3>
        <p className="text-sm text-gray-500 mt-1 line-clamp-2">
          {campaign.notes}
        </p>
        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-center gap-1 text-xs text-gray-400">
            <span>📅</span>
            <span>
              {formatDate(campaign.startDate)} - {formatDate(campaign.endDate)}
            </span>
          </div>
        </div>
        <TagList key={campaign.id} tags={campaign.tags} />
        <div className="mt-4 flex gap-2 pt-2 border-t border-gray-100">
          <button
            onClick={async () => {
              setSelectedCampaign(campaign);
              try {
                const res = await fetch(
                  `${API_ROOT_URL}/campaign-assets/${campaign.id}`,
                  {
                    headers: authenticatedJsonHeaders(),
                  },
                );

                const result = await res.json();

                let linkedAssetIds: string[] = [];

                if (result.success) {
                  linkedAssetIds = result.data
                    .map((a: any) => a.asset_id ?? a.id)
                    .filter(
                      (id: unknown) => typeof id === "string" && id.length > 0,
                    );
                  // keep linked assets resolvable for chips/picker
                  setCampaignAssets((prev) => {
                    const known = new Set(prev.map((a) => a.id));
                    const extra = result.data
                      .map((a: any) => ({
                        ...a,
                        id: String(a.asset_id ?? a.id),
                      }))
                      .filter((a: Asset) => !known.has(a.id));
                    return [...extra, ...prev];
                  });
                }

                setFormData({
                  ...campaign,
                  startDate: campaign.startDate
                    ? campaign.startDate.split("T")[0]
                    : "",
                  endDate: campaign.endDate
                    ? campaign.endDate.split("T")[0]
                    : "",
                  assetIds: linkedAssetIds,
                });
              } catch (err) {
                console.error("Failed to load linked campaign assets", err);

                // Fallback
                setFormData(campaign);
              }
              setImagePreview(campaign.image || "");
              setShowEditModal(true);
              setActiveMediaTab(campaign.video ? "video" : "image");
            }}
            className="flex-1 text-center text-sm bg-blue-50 text-blue-600 px-2 py-1.5 rounded hover:bg-blue-100 transition"
          >
            Edit
          </button>
          <button
            onClick={() => {
              setSelectedCampaign(campaign);
              setShowDetailsModal(true);
            }}
            className="flex-1 text-center text-sm bg-gray-50 text-gray-600 px-2 py-1.5 rounded hover:bg-gray-100 transition"
          >
            Details
          </button>
          <button
            onClick={() => handleDeleteCampaign(campaign)}
            className="flex-1 text-center text-sm bg-red-50 text-red-600 px-2 py-1.5 rounded hover:bg-red-100 transition"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );


  const renderProductCard = (product: Product) => (
    <div
      role="link"
      tabIndex={0}
      aria-label={`View details for ${product.name}`}
      onClick={() =>
        navigate(`/customer-manager/products/${product.id}`, {
          state: {
            product,
            returnPath: window.location.pathname + "?tab=products-only",
          },
        })
      }
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          navigate(`/customer-manager/products/${product.id}`, {
            state: {
              product,
              returnPath: window.location.pathname + "?tab=products-only",
            },
          });
        }
      }}
      className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg hover:border-purple-300 transition-all duration-300 group cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-500"
    >
      <div className="relative h-48 overflow-hidden bg-gray-100">
        {product.image ? (
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400 bg-gradient-to-br from-gray-100 to-gray-200">
            <span className="text-4xl">📦</span>
          </div>
        )}
        <div className="absolute top-2 right-2">
          <span
            className={`px-2 py-1 text-xs rounded-full ${getStatusColor(product.status)} flex items-center gap-1`}
          >
            {getStatusIcon(product.status)} {product.status}
          </span>
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-gray-800 truncate text-lg">
          {product.name}
        </h3>
        <p className="text-sm text-gray-500 mt-1 line-clamp-2">
          {product.notes}
        </p>
        <div className="mt-3">
          <div className="flex items-center gap-1 text-xs text-gray-400">
            <span>🚀 Launch: {formatDate(product.launchDate)}</span>
          </div>
        </div>
        <TagList key={product.id} tags={product.tags} />
        <div className="mt-4 flex gap-2 pt-2 border-t border-gray-100">
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/customer-manager/products/edit/${product.id}`, {
                state: {
                  product,
                  returnPath: window.location.pathname + "?tab=products-only",
                },
              });
            }}
            className="flex-1 text-center text-sm bg-blue-50 text-blue-600 px-2 py-1.5 rounded hover:bg-blue-100 transition"
          >
            Edit
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/customer-manager/products/${product.id}`, {
                state: {
                  product,
                  returnPath: window.location.pathname + "?tab=products-only",
                },
              });
            }}
            className="flex-1 text-center text-sm bg-gray-50 text-gray-600 px-2 py-1.5 rounded hover:bg-gray-100 transition"
          >
            Details
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              void handleDeleteProduct(product);
            }}
            className="flex-1 text-center text-sm bg-red-50 text-red-600 px-2 py-1.5 rounded hover:bg-red-100 transition"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );

  // ===== EVENT PAGE NAVIGATION =====
  const openCreateEventPage = () => {
    setSelectedEvent(null); // also fixes "Create" opening in Edit mode
    resetEventForm();
    setNewTag("");
    setEventMediaTab("image");
    setEventLinksLoading(false);
    setShowEventPage(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
    loadPickerProducts();
  };

  // Shared by grid cards and list rows
  const openEditEvent = async (event: Event) => {
    setSelectedEvent(event);
    setEventFormData({
      ...event,
      eventDate: event.eventDate ? event.eventDate.split("T")[0] : "",
      endDate: event.endDate ? event.endDate.split("T")[0] : "",
      productIds: [],
      assetIds: [],
    });
    setNewTag("");
    setEventMediaTab(event.video ? "video" : "image");
    setShowEventPage(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
    loadPickerProducts();

    // Load what's already linked so the chips show and Update can diff against it
    setEventLinksLoading(true);
    const links = await fetchEventLinks(event.id);
    setEventLinksLoading(false);
    if (!links.productIds || !links.assetIds) {
      toast.warning("Couldn't load this event's linked products/assets.");
    }
    setSelectedEvent((prev) =>
      prev?.id === event.id ? { ...prev, ...links } : prev,
    );
    setEventFormData((prev) =>
      prev.id === event.id
        ? {
          ...prev,
          productIds: links.productIds ?? [],
          assetIds: links.assetIds ?? [],
        }
        : prev,
    );
  };

  const closeEventPage = () => {
    setShowEventPage(false);
    setSelectedEvent(null);
    resetEventForm();
    setNewTag("");
  };

  // "Open an Event": full page with its linked products & assets
  const openEventDetails = (event: Event) => {
    setEventOrigin(null);
    setViewingProduct(null);
    setViewingAsset(null);
    setDetailLinks({
      productIds: [],
      assetIds: [],
    });
    setViewingEvent(event);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const closeEventDetails = () => {
    setViewingEvent(null);
    setViewingProduct(null);
    setViewingAsset(null);
    setDetailLinks({});
    setEventOrigin(null);
  };

  // Drill-down: Event → Product → Asset
  const openProductDetail = (product: { id: string; name: string }) => {
    setViewingAsset(null);
    setViewingProduct(product);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Opened from the event page (fromProduct = false) the breadcrumb skips the product
  const openAssetDetail = (asset: { id: string; name: string }, fromProduct = false) => {
    if (!fromProduct) setViewingProduct(null);
    setViewingAsset(asset);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const backToEvent = () => {
    setViewingProduct(null);
    setViewingAsset(null);
  };

  const backToProduct = () => setViewingAsset(null);

  const renderEventCard = (event: Event) => (
    <div
      key={event.id}
      className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300 group"
    >
      <div
        className="relative h-48 overflow-hidden bg-gray-100 cursor-pointer"
        onClick={() => openEventDetails(event)}
      >
        {event.image ? (
          <img
            src={event.image}
            alt={event.eventName}
            className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400 bg-gradient-to-br from-gray-100 to-gray-200">
            <span className="text-4xl">🎪</span>
          </div>
        )}
        <div className="absolute top-2 right-2">
          <span
            className={`px-2 py-1 text-xs rounded-full ${getStatusColor(event.status)} flex items-center gap-1`}
          >
            {getStatusIcon(event.status)} {event.status}
          </span>
        </div>
      </div>
      <div className="p-4">
        <h3
          className="font-semibold text-gray-800 truncate text-lg cursor-pointer hover:underline"
          onClick={() => openEventDetails(event)}
        >
          {event.eventName}
        </h3>
        <p className="text-sm text-gray-500 mt-1 line-clamp-2">{event.notes}</p>
        <div className="mt-3">
          <div className="flex items-center gap-1 text-xs text-gray-400">
            <span>📅 Event: {formatDate(event.eventDate)}</span>
          </div>
        </div>
        <TagList key={event.id} tags={event.tags} />
        <div className="mt-4 flex gap-2 pt-2 border-t border-gray-100">
          <button
            onClick={() => openEditEvent(event)}
            className="flex-1 text-center text-sm bg-blue-50 text-blue-600 px-2 py-1.5 rounded hover:bg-blue-100 transition"
          >
            Edit
          </button>
          <button
            onClick={() => openEventDetails(event)}
            className="flex-1 text-center text-sm bg-gray-50 text-gray-600 px-2 py-1.5 rounded hover:bg-gray-100 transition"
          >
            Details
          </button>
          <button
            onClick={() => handleDeleteEvent(event)}
            className="flex-1 text-center text-sm bg-red-50 text-red-600 px-2 py-1.5 rounded hover:bg-red-100 transition"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );

  const renderEventRow = (event: Event) => (
    <tr key={event.id} className="hover:bg-gray-50 transition-colors">
      {/* Event: thumbnail + name + notes */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 shrink-0 flex items-center justify-center">
            {event.image ? (
              <img
                src={event.image}
                alt={event.eventName}
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-xl">🎪</span>
            )}
          </div>
          <div className="min-w-0">
            <p
              className="font-medium text-gray-800 truncate max-w-[260px] cursor-pointer hover:underline"
              onClick={() => openEventDetails(event)}
            >
              {event.eventName}
            </p>
            {event.notes && (
              <p className="text-xs text-gray-500 truncate max-w-[260px]">
                {event.notes}
              </p>
            )}
          </div>
        </div>
      </td>

      {/* Date */}
      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
        {formatDate(event.eventDate)}
        {event.endDate && event.endDate !== event.eventDate && (
          <span className="text-gray-400"> – {formatDate(event.endDate)}</span>
        )}
      </td>

      {/* Status */}
      <td className="px-4 py-3">
        <span
          className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full whitespace-nowrap ${getStatusColor(event.status)}`}
        >
          {getStatusIcon(event.status)} {event.status}
        </span>
      </td>

      {/* Tags (hidden on smaller screens) */}
      <td className="px-4 py-3 hidden lg:table-cell">
        {event.tags?.length ? (
          <TagList tags={event.tags} limit={2} />
        ) : (
          <span className="text-xs text-gray-300">—</span>
        )}
      </td>

      {/* Actions */}
      <td className="px-4 py-3">
        <div className="flex justify-end gap-2">
          <button
            onClick={() => openEditEvent(event)}
            className="text-sm bg-blue-50 text-blue-600 px-3 py-1.5 rounded hover:bg-blue-100 transition"
          >
            Edit
          </button>
          <button
            onClick={() => openEventDetails(event)}
            className="text-sm bg-gray-50 text-gray-600 px-3 py-1.5 rounded hover:bg-gray-100 transition"
          >
            Details
          </button>
          <button
            onClick={() => handleDeleteEvent(event)}
            className="text-sm bg-red-50 text-red-600 px-3 py-1.5 rounded hover:bg-red-100 transition"
          >
            Delete
          </button>
        </div>
      </td>
    </tr>
  );

  // Icons shared by the event Create/Edit page and the event detail page
  const ICONS = {
    back: "M15 19l-7-7 7-7",
    calendar:
      "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
    details:
      "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
    image:
      "M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z",
    video:
      "M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z",
    upload:
      "M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12",
    close: "M6 18L18 6M6 6l12 12",
    plus: "M12 4v16m8-8H4",
    check: "M5 13l4 4L19 7",
    cube:
      "M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4",
    chevronDown: "M19 9l-7 7-7-7",
    edit:
      "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z",
    chevronRight: "M9 5l7 7-7 7",
    external:
      "M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14",
    file:
      "M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z",
  };

  const icon = (d: string, className = "w-5 h-5") => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={d} />
    </svg>
  );

  // Breadcrumb bar: Events › Event › Product › Asset (every crumb but the last is a link).
  // Rendered right under the app header (above "Library") and pinned there on scroll.
  const renderBreadcrumb = (items: { label: string; onClick?: () => void }[]) => {
    const nav = (
      <nav aria-label="Breadcrumb" className="flex items-center flex-wrap gap-1 text-sm">
        {items.map((item, i) => {
          const isLast = i === items.length - 1;
          return (
            <React.Fragment key={`${i}-${item.label}`}>
              {i > 0 && icon(ICONS.chevronRight, "w-4 h-4 text-gray-300 shrink-0")}
              {isLast || !item.onClick ? (
                <span
                  className="font-semibold text-gray-900 truncate max-w-[240px]"
                  aria-current={isLast ? "page" : undefined}
                  title={item.label}
                >
                  {item.label}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={item.onClick}
                  title={item.label}
                  className="text-gray-500 hover:underline truncate max-w-[200px]"
                  onMouseEnter={(e) => (e.currentTarget.style.color = THEME_PRIMARY)}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "")}
                >
                  {item.label}
                </button>
              )}
            </React.Fragment>
          );
        })}
      </nav>
    );

    // On the Library page: the slot itself is the full-width bar under the header
    if (breadcrumbSlot) return createPortal(nav, breadcrumbSlot);

    // Anywhere else this component is used: sticky bar at the top of the page content
    return (
      <div className="sticky top-0 z-30 px-4 py-2.5 bg-white/95 backdrop-blur-md border border-gray-200 rounded-lg shadow-sm">
        {nav}
      </div>
    );
  };

  // First crumb: the product the user came from (links back to it), otherwise "Events"
  const eventCrumbs = (event: Event) => [
    eventOrigin
      ? {
        label: eventOrigin.name,
        onClick: () => {
          window.scrollTo({ top: 0, behavior: "auto" });
          navigate(eventOrigin.path, { state: eventOrigin.state });
        },
      }
      : { label: "Events", onClick: closeEventDetails },
    { label: event.eventName, onClick: backToEvent },
  ];

  // One clickable asset tile (used on the event page and the product page)
  const renderAssetTile = (
    a: { id: string; name: string; meta?: string; isVideo?: boolean },
    onOpen: () => void,
  ) => (
    <li key={a.id}>
      <button
        type="button"
        onClick={onOpen}
        title={`Open ${a.name}`}
        className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50/50 text-left hover:bg-white hover:border-gray-300 hover:shadow-sm transition"
      >
        <span
          className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-white border border-gray-200"
          style={{ color: THEME_PRIMARY }}
        >
          {icon(a.isVideo ? ICONS.video : ICONS.image, "w-5 h-5")}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-800 truncate">{a.name}</p>
          {a.meta && <p className="text-xs text-gray-400 truncate">{a.meta}</p>}
        </div>
        {icon(ICONS.chevronRight, "w-4 h-4 text-gray-400 shrink-0")}
      </button>
    </li>
  );

  // ===== EVENT CREATE / EDIT PAGE =====
  const renderEventFormPage = () => {
    const isEdit = !!selectedEvent;

    // Same theme-coloured focus ring as the Create Campaign form
    const focusRing = {
      onFocus: (e: React.FocusEvent<HTMLElement>) => {
        e.currentTarget.style.boxShadow = `0 0 0 2px ${THEME_PRIMARY_RING}`;
        e.currentTarget.style.borderColor = THEME_PRIMARY;
      },
      onBlur: (e: React.FocusEvent<HTMLElement>) => {
        e.currentTarget.style.boxShadow = "";
        e.currentTarget.style.borderColor = "";
      },
    };

    const cardClass = "bg-white p-5 rounded-xl border border-gray-200 shadow-sm";
    const labelClass = "block text-sm font-medium text-gray-700 mb-1.5";
    const inputClass =
      "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm shadow-sm focus:outline-none";

    const sectionTitle = (title: string, subtitle: string, d: string) => (
      <>
        <div className="flex items-center gap-2 mb-1" style={{ color: THEME_PRIMARY }}>
          {icon(d)}
          <h4 className="text-base font-semibold text-gray-900">{title}</h4>
        </div>
        <p className="text-xs text-gray-500 mb-4">{subtitle}</p>
      </>
    );

    const renderDropzone = (kind: "image" | "video") => {
      const value = kind === "image" ? eventFormData.image : eventFormData.video;
      const inputRef = kind === "image" ? eventImageInputRef : eventVideoInputRef;
      return (
        <div
          key={kind}
          className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer hover:bg-gray-50 transition-colors group relative"
          style={{ borderColor: THEME_PRIMARY_RING }}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            handleEventMediaFile(e.dataTransfer.files?.[0], kind);
          }}
          onMouseEnter={(e) => (e.currentTarget.style.borderColor = THEME_PRIMARY)}
          onMouseLeave={(e) => (e.currentTarget.style.borderColor = THEME_PRIMARY_RING)}
        >
          {value ? (
            <div className="relative inline-block w-full">
              {kind === "image" ? (
                <img
                  src={value}
                  alt="Event preview"
                  className="max-h-48 mx-auto rounded-lg object-contain"
                />
              ) : (
                <video
                  src={value}
                  controls
                  className="max-h-48 mx-auto rounded-lg object-contain"
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeEventMedia(kind);
                }}
                className="absolute -top-2 -right-2 w-8 h-8 flex items-center justify-center rounded-full bg-red-600 text-white shadow-md hover:bg-red-700 transition-colors z-10"
                aria-label={`Remove ${kind}`}
              >
                {icon(ICONS.close, "w-4 h-4")}
              </button>
            </div>
          ) : (
            <div className="py-4">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform"
                style={{ backgroundColor: THEME_PRIMARY_SOFT, color: THEME_PRIMARY }}
              >
                {icon(ICONS.upload, "w-6 h-6")}
              </div>
              <p className="text-sm font-medium text-gray-900 mb-1">
                Drag and drop your event {kind}
              </p>
              <p className="text-xs text-gray-500">
                or{" "}
                <span className="font-semibold" style={{ color: THEME_PRIMARY }}>
                  browse files
                </span>{" "}
                - {kind === "image" ? "JPG, PNG up to 5 MB" : "MP4 up to 20 MB"}
              </p>
            </div>
          )}
          <input
            ref={inputRef}
            type="file"
            accept={`${kind}/*`}
            className="hidden"
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => handleEventMediaFile(e.target.files?.[0], kind)}
          />
        </div>
      );
    };

    // One "Link X (optional)" row: picker button + chips (same look as Create Campaign)
    const renderLinkField = (opts: {
      label: string;
      hint: string;
      iconPath: string;
      placeholder: string;
      noun: string;
      ids: string[];
      nameOf: (id: string) => string;
      onOpen: () => void;
      onRemove: (id: string) => void;
    }) => (
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
          <span style={{ color: THEME_PRIMARY }}>{icon(opts.iconPath, "w-4 h-4")}</span>
          {opts.label}
        </label>
        <p className="text-xs text-gray-500 mb-2">{opts.hint}</p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={opts.onOpen}
            disabled={eventLinksLoading}
            className="flex-1 flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm text-left text-gray-500 hover:border-gray-400 transition-colors shadow-sm disabled:opacity-60 disabled:cursor-wait"
          >
            <span className="truncate">
              {eventLinksLoading
                ? "Loading linked items..."
                : opts.ids.length > 0
                  ? `${opts.ids.length} ${opts.noun}(s) selected`
                  : opts.placeholder}
            </span>
            {icon(ICONS.chevronDown, "w-4 h-4 text-gray-400")}
          </button>
          <button
            type="button"
            onClick={opts.onOpen}
            disabled={eventLinksLoading}
            aria-label={`Add ${opts.noun}s`}
            className="px-3 py-2 rounded-lg border transition-colors shadow-sm shrink-0 disabled:opacity-60 disabled:cursor-wait"
            style={{
              backgroundColor: THEME_PRIMARY_SOFT,
              borderColor: THEME_PRIMARY_RING,
              color: THEME_PRIMARY,
            }}
          >
            {icon(ICONS.plus, "w-5 h-5")}
          </button>
        </div>
        {opts.ids.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {opts.ids.map((id) => (
              <span
                key={id}
                className="inline-flex items-center max-w-full gap-1.5 px-2.5 py-1 bg-gray-50 border border-gray-200 text-gray-700 rounded-md text-xs font-medium"
              >
                <span className="truncate max-w-[180px]">{opts.nameOf(id)}</span>
                <button
                  type="button"
                  onClick={() => opts.onRemove(id)}
                  className="text-gray-400 hover:text-red-500 shrink-0"
                  aria-label={`Remove ${opts.nameOf(id)}`}
                >
                  {icon(ICONS.close, "w-3.5 h-3.5")}
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    );

    return (
      <div className="space-y-5">
        {/* Page header */}
        <div>
          <button
            type="button"
            onClick={closeEventPage}
            disabled={saving}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-800 transition-colors mb-4 disabled:opacity-50"
          >
            {icon(ICONS.back, "w-4 h-4")}
            Back to Events
          </button>
          <div className="flex items-center gap-4">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 border"
              style={{
                backgroundColor: THEME_PRIMARY_SOFT,
                borderColor: THEME_PRIMARY_RING,
                color: THEME_PRIMARY,
              }}
            >
              {icon(ICONS.calendar, "w-6 h-6")}
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {isEdit ? "Edit Event" : "Create Event"}
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                {isEdit
                  ? `Update the details for "${selectedEvent?.eventName}"`
                  : "Set up your event and choose when it happens"}
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
          {/* LEFT COLUMN */}
          <div className="space-y-5">
            {/* Event details */}
            <div className={cardClass}>
              {sectionTitle(
                "Event details",
                "Provide the key information about your event.",
                ICONS.details,
              )}
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>
                    Event name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={inputClass}
                    {...focusRing}
                    value={eventFormData.eventName || ""}
                    onChange={(e) =>
                      setEventFormData((prev) => ({ ...prev, eventName: e.target.value }))
                    }
                    placeholder="e.g. Annual Conference"
                  />
                </div>
                <div>
                  <label className={labelClass}>Description</label>
                  <textarea
                    rows={3}
                    className={`${inputClass} resize-none`}
                    {...focusRing}
                    value={eventFormData.description || ""}
                    onChange={(e) =>
                      setEventFormData((prev) => ({ ...prev, description: e.target.value }))
                    }
                    placeholder="What is this event about?"
                  />
                </div>
                <div>
                  <label className={labelClass}>Notes</label>
                  <textarea
                    rows={2}
                    className={`${inputClass} resize-none`}
                    {...focusRing}
                    value={eventFormData.notes || ""}
                    onChange={(e) =>
                      setEventFormData((prev) => ({ ...prev, notes: e.target.value }))
                    }
                    placeholder="Add internal notes for your team"
                  />
                </div>
                <div>
                  <label className={labelClass}>Tags</label>
                  {(eventFormData.tags || []).length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {(eventFormData.tags || []).map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full border"
                          style={{
                            backgroundColor: THEME_PRIMARY_SOFT,
                            borderColor: THEME_PRIMARY_RING,
                            color: THEME_PRIMARY,
                          }}
                        >
                          #{tag}
                          <button
                            type="button"
                            onClick={() => removeEventTag(tag)}
                            className="opacity-60 hover:opacity-100 ml-0.5"
                            aria-label={`Remove tag ${tag}`}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addEventTag();
                        }
                      }}
                      className={`flex-1 ${inputClass}`}
                      {...focusRing}
                      placeholder="Add tags..."
                    />
                    <button
                      type="button"
                      onClick={addEventTag}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium transition-colors"
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Event media */}
            <div className={cardClass}>
              {sectionTitle(
                "Event media",
                "Upload an image or video for your event.",
                ICONS.image,
              )}
              <div className="flex gap-2 mb-4 bg-gray-50 p-1 rounded-lg w-fit border border-gray-200">
                {(["image", "video"] as const).map((kind) => {
                  const active = eventMediaTab === kind;
                  return (
                    <button
                      key={kind}
                      type="button"
                      onClick={() => setEventMediaTab(kind)}
                      className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${active
                        ? "bg-white shadow-sm border border-gray-200"
                        : "text-gray-500 hover:text-gray-700"
                        }`}
                      style={active ? { color: THEME_PRIMARY } : undefined}
                    >
                      {icon(ICONS[kind], "w-4 h-4")}
                      {kind === "image" ? "Image" : "Video"}
                    </button>
                  );
                })}
              </div>
              {renderDropzone(eventMediaTab)}
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div className="space-y-5">
            {/* Schedule & Visibility */}
            <div className={cardClass}>
              {sectionTitle(
                "Schedule & Visibility",
                "Choose when your event happens and its current status.",
                ICONS.calendar,
              )}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>Start date</label>
                    <input
                      type="date"
                      className={inputClass}
                      {...focusRing}
                      value={eventFormData.eventDate || ""}
                      onChange={(e) =>
                        setEventFormData((prev) => ({ ...prev, eventDate: e.target.value }))
                      }
                    />
                  </div>
                  <div>
                    <label className={labelClass}>End date</label>
                    <input
                      type="date"
                      min={eventFormData.eventDate || undefined}
                      className={inputClass}
                      {...focusRing}
                      value={eventFormData.endDate || ""}
                      onChange={(e) =>
                        setEventFormData((prev) => ({ ...prev, endDate: e.target.value }))
                      }
                    />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Status</label>
                  <select
                    className={inputClass}
                    {...focusRing}
                    value={eventFormData.status || "Scheduled"}
                    onChange={(e) =>
                      setEventFormData((prev) => ({
                        ...prev,
                        status: e.target.value as Event["status"],
                      }))
                    }
                  >
                    <option value="Scheduled">Scheduled</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Archived">Archived</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Link products & assets (optional) */}
            <div className={`${cardClass} space-y-4`}>
              {renderLinkField({
                label: "Link products (optional)",
                hint: "Link products featured at this event.",
                iconPath: ICONS.cube,
                placeholder: "Choose related products",
                noun: "product",
                ids: eventFormData.productIds || [],
                nameOf: (id) =>
                  pickerProducts.find((p) => p.id === id)?.name || "Product",
                onOpen: () => {
                  loadPickerProducts();
                  setShowEventProductPicker(true);
                },
                onRemove: (id) => toggleEventLink("productIds", id),
              })}
              <div className="pt-4 border-t border-gray-100">
                {renderLinkField({
                  label: "Link assets (optional)",
                  hint: "Select assets to include in this event.",
                  iconPath: ICONS.image,
                  placeholder: "Select assets",
                  noun: "asset",
                  ids: eventFormData.assetIds || [],
                  nameOf: (id) =>
                    findAssetById(id)?.original_filename || `Asset #${id}`,
                  onOpen: () => setShowEventAssetPicker(true),
                  onRemove: (id) => toggleEventLink("assetIds", id),
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Footer actions (stays visible while scrolling) */}
        <div className="sticky bottom-0 z-10 bg-white border border-gray-200 rounded-xl shadow-sm px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <span className="text-red-500 font-bold text-sm">*</span> Required fields
            <span className="mx-1">•</span>
            You can edit these details later.
          </div>
          <div className="flex gap-3 w-full sm:w-auto">
            <button
              type="button"
              onClick={closeEventPage}
              disabled={saving}
              className="flex-1 sm:flex-none px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 hover:text-gray-900 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={isEdit ? handleUpdateEvent : handleCreateEvent}
              disabled={saving}
              className="flex-1 sm:flex-none px-5 py-2.5 text-white rounded-xl hover:opacity-90 disabled:opacity-70 disabled:cursor-not-allowed transition-opacity font-medium text-sm shadow-sm flex items-center justify-center gap-2"
              style={{ backgroundColor: THEME_PRIMARY }}
            >
              {saving ? (
                <>
                  <span
                    className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
                    aria-hidden="true"
                  />
                  {isEdit ? "Updating..." : "Creating..."}
                </>
              ) : (
                <>
                  {icon(isEdit ? ICONS.check : ICONS.plus, "w-4 h-4")}
                  {isEdit ? "Update Event" : "Create Event"}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ===== EVENT DETAIL PAGE ("Open an Event") =====
  const renderEventDetailPage = (event: Event) => {
    const cardClass = "bg-white p-5 rounded-xl border border-gray-200 shadow-sm";
    const { productIds, assetIds } = detailLinks;

    const linkedProducts = (productIds || []).map((id) => {
      const p = pickerProducts.find((x) => x.id === id);
      return { id, name: p?.name || "Product", status: p?.status };
    });

    const linkedAssets = (assetIds || []).map((id) => {
      const a = findAssetById(id);
      const name = a?.original_filename || `Asset #${id}`;
      return {
        id,
        name,
        meta: [a?.status, a?.container].filter(Boolean).join(" • "),
        isVideo: isVideoAsset(name, a?.container),
      };
    });

    // Loading / error / empty states shared by both linked sections
    const renderLinkedState = (
      ids: string[] | undefined,
      noun: "products" | "assets",
      list: React.ReactNode,
    ) => {
      if (detailLinksLoading) {
        return (
          <p className="text-sm text-gray-400 text-center py-8">
            Loading linked {noun}...
          </p>
        );
      }
      if (ids === undefined) {
        return (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            Couldn't load linked {noun}. Please try again later.
          </p>
        );
      }
      if (ids.length === 0) {
        return (
          <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
            <p className="text-sm text-gray-500">No {noun} linked yet.</p>
            <button
              type="button"
              onClick={() => openEditEvent(event)}
              className="mt-2 text-sm font-medium hover:underline"
              style={{ color: THEME_PRIMARY }}
            >
              + Link {noun}
            </button>
          </div>
        );
      }
      return list;
    };

    const sectionHeader = (title: string, d: string, count?: number) => (
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span style={{ color: THEME_PRIMARY }}>{icon(d)}</span>
          <h4 className="text-base font-semibold text-gray-900">{title}</h4>
          {count !== undefined && !detailLinksLoading && (
            <span
              className="min-w-[22px] text-center rounded-full px-1.5 text-xs leading-5"
              style={{ backgroundColor: THEME_PRIMARY_SOFT, color: THEME_PRIMARY }}
            >
              {count}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => openEditEvent(event)}
          className="text-xs font-medium hover:underline"
          style={{ color: THEME_PRIMARY }}
        >
          Manage
        </button>
      </div>
    );

    const emptyDash = <span className="text-gray-400">—</span>;

    return (
      <div className="space-y-5">
        {renderBreadcrumb(eventCrumbs(event))}

        {/* Hero: media + title + Edit */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="relative h-56 sm:h-64 bg-gray-100">
            {event.video ? (
              <video
                src={event.video}
                controls
                className="w-full h-full object-contain bg-black"
              />
            ) : event.image ? (
              <img
                src={event.image}
                alt={event.eventName}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                <span className="text-6xl">🎪</span>
              </div>
            )}
          </div>
          <div className="p-5 flex flex-col sm:flex-row sm:items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-2xl font-bold text-gray-900 break-words">
                  {event.eventName}
                </h2>
                <span
                  className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${getStatusColor(event.status)}`}
                >
                  {getStatusIcon(event.status)} {event.status}
                </span>
              </div>
              <p className="mt-2 flex items-center gap-1.5 text-sm text-gray-500">
                {icon(ICONS.calendar, "w-4 h-4")}
                {formatDate(event.eventDate)}
                {event.endDate &&
                  event.endDate !== event.eventDate &&
                  ` – ${formatDate(event.endDate)}`}
              </p>
            </div>
            <button
              type="button"
              onClick={() => openEditEvent(event)}
              className="shrink-0 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-xl shadow-sm hover:opacity-90 transition-opacity"
              style={{ backgroundColor: THEME_PRIMARY }}
            >
              {icon(ICONS.edit, "w-4 h-4")}
              Edit event
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
          {/* About */}
          <div className={cardClass}>
            <div className="flex items-center gap-2 mb-4">
              <span style={{ color: THEME_PRIMARY }}>{icon(ICONS.details)}</span>
              <h4 className="text-base font-semibold text-gray-900">About</h4>
            </div>
            <dl className="space-y-4 text-sm">
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Description
                </dt>
                <dd className="mt-1 text-gray-800 whitespace-pre-line">
                  {event.description || emptyDash}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Notes
                </dt>
                <dd className="mt-1 text-gray-800 whitespace-pre-line">
                  {event.notes || emptyDash}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Tags
                </dt>
                <dd className="mt-1 flex flex-wrap gap-1.5">
                  {event.tags?.length
                    ? event.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 text-xs font-medium rounded-full border"
                        style={{
                          backgroundColor: THEME_PRIMARY_SOFT,
                          borderColor: THEME_PRIMARY_RING,
                          color: THEME_PRIMARY,
                        }}
                      >
                        #{tag}
                      </span>
                    ))
                    : emptyDash}
                </dd>
              </div>
            </dl>
          </div>

          {/* Linked products & assets */}
          <div className="lg:col-span-2 space-y-5">
            <div className={cardClass}>
              {sectionHeader("Linked products", ICONS.cube, productIds?.length)}
              {renderLinkedState(
                productIds,
                "products",
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {linkedProducts.map((p) => (
                    <li key={p.id}>
                      <button
                        type="button"
                        onClick={() => openProductDetail({ id: p.id, name: p.name })}
                        title={`Open ${p.name}`}
                        className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50/50 text-left hover:bg-white hover:border-gray-300 hover:shadow-sm transition"
                      >
                        <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 text-lg bg-white border border-gray-200">
                          📦
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-800 truncate">{p.name}</p>
                          {p.status && <p className="text-xs text-gray-400">{p.status}</p>}
                        </div>
                        {icon(ICONS.chevronRight, "w-4 h-4 text-gray-400 shrink-0")}
                      </button>
                    </li>
                  ))}
                </ul>,
              )}
            </div>

            <div className={cardClass}>
              {sectionHeader("Linked assets", ICONS.image, assetIds?.length)}
              {renderLinkedState(
                assetIds,
                "assets",
                <ul className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
                  {linkedAssets.map((a) =>
                    renderAssetTile(a, () => openAssetDetail({ id: a.id, name: a.name })),
                  )}
                </ul>,
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ===== PRODUCT DETAIL PAGE (Event → Product) =====
  const renderProductDetailPage = (event: Event, product: { id: string; name: string }) => {
    const cardClass = "bg-white p-5 rounded-xl border border-gray-200 shadow-sm";
    const cached = pickerProducts.find((p) => p.id === product.id);
    const info: Partial<Product> =
      cached?.details ?? products.find((p) => p.id === product.id) ?? {};
    const name = info.name || cached?.name || product.name;
    const status = normalizeStatus(info.status || cached?.status || "");
    const emptyDash = <span className="text-gray-400">—</span>;

    const tiles = (productPageAssets || []).map((a) => ({
      id: a.id,
      name: a.original_filename || `Asset #${a.id}`,
      meta: [a.status, a.container].filter(Boolean).join(" • "),
      isVideo: isVideoAsset(a.original_filename, a.container),
    }));

    return (
      <div className="space-y-5">
        {renderBreadcrumb([...eventCrumbs(event), { label: name }])}

        {/* Product header */}
        <div className={`${cardClass} flex items-start gap-4`}>
          <div
            className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0 border text-2xl"
            style={{ backgroundColor: THEME_PRIMARY_SOFT, borderColor: THEME_PRIMARY_RING }}
          >
            📦
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-2xl font-bold text-gray-900 break-words">{name}</h2>
              {status && (
                <span
                  className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${getStatusColor(status)}`}
                >
                  {getStatusIcon(status)} {status}
                </span>
              )}
            </div>
            {info.launchDate && (
              <p className="mt-2 flex items-center gap-1.5 text-sm text-gray-500">
                {icon(ICONS.calendar, "w-4 h-4")}
                Launch: {formatDate(info.launchDate)}
                {info.endDate && ` – Ends: ${formatDate(info.endDate)}`}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-400">
              Linked to event "{event.eventName}"
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
          {/* About */}
          <div className={cardClass}>
            <div className="flex items-center gap-2 mb-4">
              <span style={{ color: THEME_PRIMARY }}>{icon(ICONS.details)}</span>
              <h4 className="text-base font-semibold text-gray-900">About</h4>
            </div>
            <dl className="space-y-4 text-sm">
              {(
                [
                  ["Description", info.description],
                  ["Notes", info.notes],
                ] as const
              ).map(([label, value]) => (
                <div key={label}>
                  <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {label}
                  </dt>
                  <dd className="mt-1 text-gray-800 whitespace-pre-line">
                    {value || emptyDash}
                  </dd>
                </div>
              ))}
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Tags
                </dt>
                <dd className="mt-1 flex flex-wrap gap-1.5">
                  {info.tags?.length
                    ? info.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 text-xs font-medium rounded-full border"
                        style={{
                          backgroundColor: THEME_PRIMARY_SOFT,
                          borderColor: THEME_PRIMARY_RING,
                          color: THEME_PRIMARY,
                        }}
                      >
                        #{tag}
                      </span>
                    ))
                    : emptyDash}
                </dd>
              </div>
            </dl>
          </div>

          {/* All assets of this product */}
          <div className={`${cardClass} lg:col-span-2`}>
            <div className="flex items-center gap-2 mb-4">
              <span style={{ color: THEME_PRIMARY }}>{icon(ICONS.image)}</span>
              <h4 className="text-base font-semibold text-gray-900">Assets</h4>
              {!productPageAssetsLoading && productPageAssets && (
                <span
                  className="min-w-[22px] text-center rounded-full px-1.5 text-xs leading-5"
                  style={{ backgroundColor: THEME_PRIMARY_SOFT, color: THEME_PRIMARY }}
                >
                  {productPageAssets.length}
                </span>
              )}
            </div>
            {productPageAssetsLoading ? (
              <p className="text-sm text-gray-400 text-center py-8">Loading assets...</p>
            ) : productPageAssets === undefined ? (
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Couldn't load this product's assets. Please try again later.
              </p>
            ) : tiles.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                <p className="text-sm text-gray-500">No assets are linked to this product yet.</p>
              </div>
            ) : (
              <ul className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
                {tiles.map((a) =>
                  renderAssetTile(a, () => openAssetDetail({ id: a.id, name: a.name }, true)),
                )}
              </ul>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ===== ASSET DETAIL PAGE (Event → [Product →] Asset) =====
  const renderAssetDetailPage = (event: Event, assetRef: { id: string; name: string }) => {
    const cardClass = "bg-white p-5 rounded-xl border border-gray-200 shadow-sm";
    // Merge everything we know: list data / product-page row + the fetched record
    const known =
      (productPageAssets || []).find((a) => a.id === assetRef.id) ??
      findAssetById(assetRef.id);
    const a: Record<string, any> = { ...(known || {}), ...(assetDetail || {}) };
    const name: string = a.original_filename || assetRef.name;
    const container = a.container ?? null;
    const video = isVideoAsset(name, container);
    // Images can use a preview/thumbnail link; videos need a playable one
    const mediaUrl = pickMediaUrl(a, !video);
    const posterRaw = a.thumbnail_url ?? a.thumbnailUrl;
    const poster = typeof posterRaw === "string" ? posterRaw : undefined;
    const image = !video && (isImageAsset(name, container) || !!mediaUrl);

    const crumbs = [
      ...eventCrumbs(event),
      ...(viewingProduct ? [{ label: viewingProduct.name, onClick: backToProduct }] : []),
      { label: name },
    ];

    const rows = (
      [
        ["File name", name],
        ["Type", video ? "Video" : image ? "Image" : "File"],
        ["Status", a.status || ""],
        ["Format", [a.container, a.codec].filter(Boolean).join(" / ")],
        ["Resolution", a.width && a.height ? `${a.width} × ${a.height}` : ""],
        ["Duration", formatDuration(Number(a.duration_sec))],
        ["File size", formatBytes(Number(a.file_size_bytes))],
        ["Uploaded", a.created_at ? formatDate(String(a.created_at)) : ""],
      ] as [string, string][]
    ).filter(([, value]) => value);

    return (
      <div className="space-y-5">
        {renderBreadcrumb(crumbs)}

        <div>
          <h2 className="text-2xl font-bold text-gray-900 break-words">{name}</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {viewingProduct
              ? `Asset of "${viewingProduct.name}" · Event "${event.eventName}"`
              : `Asset linked to event "${event.eventName}"`}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
          {/* Player / large image */}
          <div className="lg:col-span-2 bg-black rounded-xl overflow-hidden shadow-sm flex items-center justify-center min-h-[320px]">
            {assetDetailLoading && !mediaUrl ? (
              <p className="text-sm text-gray-300 py-24">Loading preview...</p>
            ) : mediaUrl && video ? (
              <video
                key={mediaUrl}
                src={mediaUrl}
                poster={poster}
                controls
                className="w-full max-h-[70vh] bg-black"
              />
            ) : mediaUrl ? (
              <img
                src={mediaUrl}
                alt={name}
                className="max-w-full max-h-[70vh] object-contain"
              />
            ) : (
              <div className="text-center py-20 px-6">
                <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center bg-white/10 text-white mb-3">
                  {icon(video ? ICONS.video : ICONS.file, "w-7 h-7")}
                </div>
                <p className="text-sm text-gray-200 font-medium">
                  Preview isn't available for this asset
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  The server didn't return a viewable link for it.
                </p>
              </div>
            )}
          </div>

          {/* Details */}
          <div className={cardClass}>
            <div className="flex items-center gap-2 mb-4">
              <span style={{ color: THEME_PRIMARY }}>{icon(ICONS.details)}</span>
              <h4 className="text-base font-semibold text-gray-900">Asset details</h4>
            </div>
            <dl className="space-y-3 text-sm">
              {rows.map(([label, value]) => (
                <div key={label} className="flex justify-between gap-4">
                  <dt className="text-gray-500 shrink-0">{label}</dt>
                  <dd className="text-gray-800 text-right break-all">{value}</dd>
                </div>
              ))}
            </dl>
            {mediaUrl && (
              <a
                href={mediaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-xl shadow-sm hover:opacity-90 transition-opacity"
                style={{ backgroundColor: THEME_PRIMARY }}
              >
                {icon(ICONS.external, "w-4 h-4")}
                Open full size
              </a>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ===== JSX =====
  const isEventPageOpen = activeTab === "events" && showEventPage;
  // Prefer the fresh copy from the list (it updates after Edit); fall back to the snapshot
  const currentViewEvent = viewingEvent
    ? events.find((e) => e.id === viewingEvent.id) ?? viewingEvent
    : null;
  const isEventDetailOpen = activeTab === "events" && !!currentViewEvent;

  return (
    <div className="space-y-6">
      {isEventPageOpen ? (
        renderEventFormPage()
      ) : isEventDetailOpen && currentViewEvent ? (
        viewingAsset ? (
          renderAssetDetailPage(currentViewEvent, viewingAsset)
        ) : viewingProduct ? (
          renderProductDetailPage(currentViewEvent, viewingProduct)
        ) : (
          renderEventDetailPage(currentViewEvent)
        )
      ) : (
        <>
          {!view || view === "summary" ? (
            <>
              {/* Header Banner */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <div className="text-4xl">📋</div>
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-purple-900 text-lg">
                          Campaigns & Events Management
                        </h3>
                        <p className="text-sm text-purple-700 mt-1">
                          Create and manage Campaigns, Products, and Events with
                          Public/Private Archive visibility.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-4 mt-3">
                      <div className="flex items-center gap-1 text-xs text-purple-600">
                        <span>✓</span> Campaign Section
                      </div>
                      <div className="flex items-center gap-1 text-xs text-purple-600">
                        <span>✓</span> Product Section
                      </div>
                      <div className="flex items-center gap-1 text-xs text-purple-600">
                        <span>✓</span> Events Section
                      </div>
                      <div className="flex items-center gap-1 text-xs text-purple-600">
                        <span>✓</span> Public/Private Archive
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <div className="text-2xl mb-2">🎯</div>
                  <div className="text-2xl font-bold text-gray-800">
                    {campaigns.length}
                  </div>
                  <div className="text-sm text-gray-500">Campaigns</div>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <div className="text-2xl mb-2">📦</div>
                  <div className="text-2xl font-bold text-gray-800">
                    {products.length}
                  </div>
                  <div className="text-sm text-gray-500">Products</div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <div className="text-2xl mb-2">🎪</div>
                  <div className="text-2xl font-bold text-gray-800">
                    {events.length}
                  </div>
                  <div className="text-sm text-gray-500">Events</div>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <div className="text-2xl mb-2">✅</div>
                  <div className="text-2xl font-bold text-green-600">
                    {campaigns.filter((c) => c.status === "Active").length +
                      products.filter((p) => p.status === "Active").length +
                      events.filter((e) => e.status === "Active").length}
                  </div>
                  <div className="text-sm text-gray-500">Active Items</div>
                </div>
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
                  <div className="text-2xl mb-2">📦</div>
                  <div className="text-2xl font-bold text-gray-700">
                    {archiveCounts.total}
                  </div>
                  <div className="text-sm text-gray-500 mb-2">Archived Items</div>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <div className="text-2xl mb-2">🌐</div>
                  <div className="text-2xl font-bold text-blue-600">
                    {archiveVisibility === "Public" ? "Public" : "Private"}
                  </div>
                  <div className="text-sm text-gray-500">Current View</div>
                </div>
              </div>
            </>
          ) : (
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                {singleViewHeading[view]?.title}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {singleViewHeading[view]?.subtitle}
              </p>
            </div>
          )}

          {/* Tab Navigation */}
          {!view && (
            <div className="border-b border-gray-200">
              <nav className="flex gap-6">
                <button
                  onClick={() => setActiveTab("campaigns")}
                  className={`pb-3 px-1 font-medium transition ${activeTab === "campaigns"
                    ? "border-b-2 border-purple-600 text-purple-600"
                    : "text-gray-500 hover:text-gray-700"
                    }`}
                >
                  🎯 Campaigns ({campaignsTotal})
                </button>
                <button
                  onClick={() => setActiveTab("products")}
                  className={`pb-3 px-1 font-medium transition ${activeTab === "products"
                    ? "border-b-2 border-purple-600 text-purple-600"
                    : "text-gray-500 hover:text-gray-700"
                    }`}
                >
                  📦 Products ({productTotal})
                </button>

                <button
                  onClick={() => setActiveTab("events")}
                  className={`pb-3 px-1 font-medium transition ${activeTab === "events"
                    ? "border-b-2 border-purple-600 text-purple-600"
                    : "text-gray-500 hover:text-gray-700"
                    }`}
                >
                  🎪 Events ({eventsTotal})
                </button>
              </nav>
            </div>
          )}

          {/* Filters and Actions Bar */}
          {view !== "summary" && (
            <>
              <div className="flex justify-between items-center flex-wrap gap-4">
                <div className="flex gap-3 flex-wrap">
                  {activeTab === "products" ? (
                    <>
                      <div className="relative">
                        <select
                          value={productStatusFilter}
                          onChange={(e) => setProductStatusFilter(e.target.value)}
                          className="appearance-none min-w-[170px] h-11 px-4 pr-10 bg-white border border-gray-300 rounded-xl text-sm font-medium text-gray-700 shadow-sm cursor-pointer transition-all duration-200 hover:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        >
                          <option value="all">All Status</option>
                          <option value="Active">Active</option>
                          <option value="Archived">Archived</option>
                        </select>
                        <svg
                          className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                      <div className="relative">
                        <input
                          type="text"
                          value={productSearchInput}
                          onChange={(e) => setProductSearchInput(e.target.value)}
                          placeholder="Search products..."
                          className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 w-64"
                        />
                        <span className="absolute left-3 top-2.5 text-gray-400">
                          🔍
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="relative">
                        <select
                          value={filterStatus}
                          onChange={(e) => {
                            setFilterStatus(e.target.value);
                            setCampaignsPage(1);
                            setEventsPage(1);
                          }}
                          className="appearance-none min-w-[170px] h-11 px-4 pr-10 bg-white border border-gray-300 rounded-xl text-sm font-medium text-gray-700 shadow-sm cursor-pointer transition-all duration-200 hover:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        >
                          <option value="all">All Status</option>
                          <option value="Active">Active</option>
                          <option value="Archived">Archived</option>
                        </select>
                        <svg
                          className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                      <div className="relative">
                        <input
                          type="text"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          placeholder="Search..."
                          className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 w-64"
                        />
                        <span className="absolute left-3 top-2.5 text-gray-400">
                          🔍
                        </span>
                        {searchTerm && (
                          <button
                            type="button"
                            onClick={() => setSearchTerm("")}
                            className="absolute right-2 top-2.5 text-gray-400 hover:text-gray-600"
                            aria-label="Clear search"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {activeTab === "campaigns" && (
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition flex items-center gap-2 shadow-sm"
                  >
                    <span className="text-xl">+</span> Create Campaign
                  </button>
                )}
                {activeTab === "products" && (
                  <div className="flex items-center gap-3">
                    {/* Grid/List Toggle */}
                    <div className="flex items-center bg-white border border-gray-300 rounded-lg p-1 shadow-sm">
                      <button
                        type="button"
                        onClick={() => setProductViewMode("grid")}
                        className={`p-1.5 rounded-md transition-colors ${productViewMode === "grid"
                          ? "bg-purple-100 text-purple-600"
                          : "text-gray-400 hover:text-gray-600"
                          }`}
                        title="Grid View"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => setProductViewMode("list")}
                        className={`p-1.5 rounded-md transition-colors ${productViewMode === "list"
                          ? "bg-purple-100 text-purple-600"
                          : "text-gray-400 hover:text-gray-600"
                          }`}
                        title="List View"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                      </button>
                    </div>

                    {/* Create Product Button */}
                    <button
                      onClick={() =>
                        navigate("/customer-manager/products/create", {
                          state: { returnPath: window.location.pathname + "?tab=products-only" },
                        })
                      }
                      className="px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition flex items-center gap-2 shadow-sm"
                    >
                      <span className="text-xl">+</span> Create Product
                    </button>
                  </div>
                )}
                {activeTab === "events" && (
                  <div className="flex items-center gap-3">
                    {/* Grid / List toggle */}
                    <div
                      role="group"
                      aria-label="Events layout"
                      className="inline-flex rounded-lg border border-gray-300 bg-white p-1 shadow-sm"
                    >
                      {(
                        [
                          {
                            key: "grid",
                            label: "Grid",
                            d: "M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z",
                          },
                          {
                            key: "list",
                            label: "List",
                            d: "M4 6h16M4 10h16M4 14h16M4 18h16",
                          },
                        ] as const
                      ).map((opt) => {
                        const active = eventsViewMode === opt.key;
                        return (
                          <button
                            key={opt.key}
                            type="button"
                            onClick={() => setEventsViewMode(opt.key)}
                            aria-pressed={active}
                            title={`${opt.label} view`}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${active ? "shadow-sm" : "text-gray-500 hover:text-gray-700"
                              }`}
                            style={
                              active
                                ? { backgroundColor: THEME_PRIMARY_SOFT, color: THEME_PRIMARY }
                                : undefined
                            }
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={opt.d} />
                            </svg>
                            <span className="hidden sm:inline">{opt.label}</span>
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={openCreateEventPage}
                      className="px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition flex items-center gap-2 shadow-sm"
                    >
                      <span className="text-xl">+</span> Create Event
                    </button>
                  </div>
                )}
              </div>

              {/* Content Grids */}
              {activeTab === "campaigns" &&
                (campaignsLoading ? (
                  <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <div className="text-6xl mb-3 animate-pulse">🎯</div>
                    <p className="text-gray-500">Loading campaigns...</p>
                  </div>
                ) : filteredCampaigns.length > 0 ? (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 content-start">
                      {filteredCampaigns.map((campaign) =>
                        renderCampaignCard(campaign),
                      )}
                    </div>
                    <PaginationControls
                      page={campaignsPage}
                      totalPages={campaignsTotalPages}
                      total={campaignsTotal}
                      pageSize={campaignsPageSize}
                      loading={campaignsLoading}
                      onPageChange={setCampaignsPage}
                      onPageSizeChange={(size) => {
                        setCampaignsPageSize(size);
                        setCampaignsPage(1);
                      }}
                    />
                  </>
                ) : (
                  <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <div className="text-6xl mb-3">🎯</div>
                    <p className="text-gray-500">No campaigns found</p>
                    <p className="text-sm text-gray-400 mt-1">
                      Click "Create Campaign" to get started
                    </p>
                  </div>
                ))}

              {activeTab === "products" &&
                (filteredProducts.length > 0 ? (
                  <>
                    {productViewMode === "grid" ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {filteredProducts.map((product) =>
                          renderProductCard(product),
                        )}
                      </div>
                    ) : (
                      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                        <table className="w-full text-left text-sm text-gray-600">
                          <thead className="bg-gray-50 border-b border-gray-200 text-gray-500">
                            <tr>
                              <th className="px-6 py-4 font-medium">Product Name</th>
                              <th className="px-6 py-4 font-medium">Launch Date</th>
                              <th className="px-6 py-4 font-medium">Status</th>
                              <th className="px-6 py-4 font-medium">Tags</th>
                              <th className="px-6 py-4 font-medium text-right">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {filteredProducts.map((product) => (
                              <tr key={product.id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-4">
                                  <div className="flex items-center gap-3">
                                    {product.image ? (
                                      <img src={product.image} alt="" className="w-10 h-10 rounded object-cover" />
                                    ) : (
                                      <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center text-xl">📦</div>
                                    )}
                                    <div>
                                      <p className="font-medium text-gray-900">{product.name}</p>
                                      <p className="text-xs text-gray-500 truncate max-w-[200px]">{product.description || "No description"}</p>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4">{formatDate(product.launchDate)}</td>
                                <td className="px-6 py-4">
                                  <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(product.status)}`}>
                                    {product.status}
                                  </span>
                                </td>
                                <td className="px-6 py-4">
                                  <TagList tags={product.tags} />
                                </td>
                                <td className="px-6 py-4 text-right">
                                  <div className="flex items-center justify-end gap-2">
                                    <button
                                      onClick={() =>
                                        navigate(`/customer-manager/products/edit/${product.id}`, {
                                          state: { returnPath: window.location.pathname + "?tab=products-only" },
                                        })
                                      }
                                      className="p-2 text-blue-600 hover:text-blue-800 rounded-lg hover:bg-blue-50 transition"
                                      title="Edit"
                                    >
                                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                      </svg>
                                    </button>
                                    <button
                                      onClick={() => handleDeleteProduct(product)}
                                      className="p-2 text-red-600 hover:text-red-800 rounded-lg hover:bg-red-50 transition"
                                      title="Delete"
                                    >
                                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <div className="flex items-center justify-end flex-wrap gap-3 mt-4">
                      <span className="text-sm text-gray-500">
                        Showing {(productPage - 1) * productLimit + 1}-
                        {Math.min(productPage * productLimit, productTotal)} of{" "}
                        {productTotal}
                      </span>
                      <button
                        disabled={productPage <= 1}
                        onClick={() => setProductPage((p) => p - 1)}
                        className="px-3 py-1.5 border rounded-lg disabled:opacity-40 text-sm"
                      >
                        Prev
                      </button>
                      <button
                        disabled={productPage >= productTotalPages}
                        onClick={() => setProductPage((p) => p + 1)}
                        className="px-3 py-1.5 border rounded-lg disabled:opacity-40 text-sm"
                      >
                        Next
                      </button>
                      <select
                        value={productLimit}
                        onChange={(e) => setProductLimit(Number(e.target.value))}
                        className="px-2 py-1.5 border rounded-lg text-sm"
                      >
                        {[12, 24, 48, 96].map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <div className="text-6xl mb-3">📦</div>
                    <p className="text-gray-500">No products found</p>
                    <p className="text-sm text-gray-400 mt-1">
                      Click "Create Product" to get started
                    </p>
                  </div>
                ))}

              {activeTab === "events" &&
                (eventsLoading ? (
                  <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <div className="text-6xl mb-3 animate-pulse">🎪</div>
                    <p className="text-gray-500">Loading events...</p>
                  </div>
                ) : filteredEvents.length > 0 ? (
                  <>
                    {eventsViewMode === "grid" ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 content-start">
                        {filteredEvents.map((event) => renderEventCard(event))}
                      </div>
                    ) : (
                      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                Event
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                Date
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                Status
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                                Tags
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {filteredEvents.map((event) => renderEventRow(event))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <PaginationControls
                      page={eventsPage}
                      totalPages={eventsTotalPages}
                      total={eventsTotal}
                      pageSize={eventsPageSize}
                      loading={eventsLoading}
                      onPageChange={setEventsPage}
                      onPageSizeChange={(size) => {
                        setEventsPageSize(size);
                        setEventsPage(1);
                      }}
                    />
                  </>
                ) : (
                  <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <div className="text-6xl mb-3">🎪</div>
                    <p className="text-gray-500">No events found</p>
                    <p className="text-sm text-gray-400 mt-1">
                      Click "Create Event" to get started
                    </p>
                  </div>
                ))}
            </>
          )}
        </>
      )}

      {/* ===== MODALS ===== */}
      {/* Create Campaign Modal */}
      {showCreateModal && !showAssetPicker && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 sm:p-6">
          <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[92vh] flex flex-col shadow-xl overflow-hidden">
            {/* Header */}
            <div className="px-6 py-5 border-b border-gray-100 flex items-start justify-between shrink-0 bg-white">
              <div className="flex items-center gap-4">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 border"
                  style={{
                    backgroundColor: THEME_PRIMARY_SOFT,
                    borderColor: THEME_PRIMARY_RING,
                    color: THEME_PRIMARY,
                  }}
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    Create Campaign
                  </h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Set up your campaign and choose when it goes live
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-2 -mr-2 rounded-full hover:bg-gray-100"
                aria-label="Close"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="p-5 sm:p-6 overflow-y-auto flex-1 bg-gray-50/30">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
                {/* LEFT COLUMN */}
                <div className="space-y-5">
                  {/* Campaign Details */}
                  <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-1">
                      <svg
                        className="w-5 h-5"
                        style={{ color: THEME_PRIMARY }}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <h4 className="text-base font-semibold text-gray-900">
                        Campaign details
                      </h4>
                    </div>
                    <p className="text-xs text-gray-500 mb-4">
                      Provide the key information about your campaign.
                    </p>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Campaign title{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm shadow-sm focus:outline-none"
                          onFocus={(e) => {
                            e.currentTarget.style.boxShadow = `0 0 0 2px ${THEME_PRIMARY_RING}`;
                            e.currentTarget.style.borderColor = THEME_PRIMARY;
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.boxShadow = "";
                            e.currentTarget.style.borderColor = "";
                          }}
                          value={formData.title}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              title: e.target.value,
                            }))
                          }
                          placeholder="e.g. Summer Collection Launch"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Description
                        </label>
                        <textarea
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm shadow-sm resize-none focus:outline-none"
                          rows={3}
                          onFocus={(e) => {
                            e.currentTarget.style.boxShadow = `0 0 0 2px ${THEME_PRIMARY_RING}`;
                            e.currentTarget.style.borderColor = THEME_PRIMARY;
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.boxShadow = "";
                            e.currentTarget.style.borderColor = "";
                          }}
                          value={formData.description}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              description: e.target.value,
                            }))
                          }
                          placeholder="What is this campaign about?"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Notes
                        </label>
                        <textarea
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm shadow-sm resize-none focus:outline-none"
                          rows={2}
                          onFocus={(e) => {
                            e.currentTarget.style.boxShadow = `0 0 0 2px ${THEME_PRIMARY_RING}`;
                            e.currentTarget.style.borderColor = THEME_PRIMARY;
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.boxShadow = "";
                            e.currentTarget.style.borderColor = "";
                          }}
                          value={formData.notes}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              notes: e.target.value,
                            }))
                          }
                          placeholder="Add internal notes for your team"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Tags
                        </label>
                        <div className="flex flex-wrap gap-2 mb-2">
                          {formData.tags?.map((tag) => (
                            <span
                              key={tag}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full border"
                              style={{
                                backgroundColor: THEME_PRIMARY_SOFT,
                                borderColor: THEME_PRIMARY_RING,
                                color: THEME_PRIMARY,
                              }}
                            >
                              #{tag}
                              <button
                                onClick={() => removeTag(tag)}
                                className="opacity-60 hover:opacity-100 ml-0.5"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newTag}
                            onChange={(e) => setNewTag(e.target.value)}
                            onKeyPress={(e) => e.key === "Enter" && addTag()}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            placeholder="Add tags..."
                          />
                          <button
                            onClick={addTag}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium transition-colors"
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Media */}
                  <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-1">
                      <svg
                        className="w-5 h-5"
                        style={{ color: THEME_PRIMARY }}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <h4 className="text-base font-semibold text-gray-900">
                        Campaign media
                      </h4>
                    </div>
                    <p className="text-xs text-gray-500 mb-4">
                      Upload an image or video for your campaign.
                    </p>

                    <div className="flex gap-2 mb-4 bg-gray-50 p-1 rounded-lg w-fit border border-gray-200">
                      <button
                        type="button"
                        onClick={() => setActiveMediaTab("image")}
                        className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${activeMediaTab === "image"
                          ? "bg-white shadow-sm border border-gray-200"
                          : "text-gray-500 hover:text-gray-700"
                          }`}
                        style={
                          activeMediaTab === "image"
                            ? { color: THEME_PRIMARY }
                            : undefined
                        }
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        Image
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveMediaTab("video")}
                        className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${activeMediaTab === "video"
                          ? "bg-white shadow-sm border border-gray-200"
                          : "text-gray-500 hover:text-gray-700"
                          }`}
                        style={
                          activeMediaTab === "video"
                            ? { color: THEME_PRIMARY }
                            : undefined
                        }
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                          />
                        </svg>
                        Video
                      </button>
                    </div>

                    {activeMediaTab === "image" && (
                      <div
                        className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer hover:bg-gray-50 transition-colors group relative overflow-hidden"
                        style={{ borderColor: THEME_PRIMARY_RING }}
                        onClick={() => imageInputRef.current?.click()}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.borderColor = THEME_PRIMARY)
                        }
                        onMouseLeave={(e) =>
                        (e.currentTarget.style.borderColor =
                          THEME_PRIMARY_RING)
                        }
                      >
                        {imagePreview ? (
                          <div className="relative inline-block w-full">
                            <img
                              src={imagePreview}
                              alt="Preview"
                              className="max-h-48 mx-auto rounded-lg object-contain"
                            />
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeCampaignImage();
                              }}
                              className="absolute -top-2 -right-2 w-8 h-8 flex items-center justify-center rounded-full bg-red-600 text-white shadow-md hover:bg-red-700 transition-colors z-10"
                              aria-label="Remove image"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            </button>
                          </div>
                        ) : (
                          <div className="py-4">
                            <div
                              className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform"
                              style={{ backgroundColor: THEME_PRIMARY_SOFT }}
                            >
                              <svg
                                className="w-6 h-6"
                                style={{ color: THEME_PRIMARY }}
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                />
                              </svg>
                            </div>
                            <p className="text-sm font-medium text-gray-900 mb-1">
                              Drag and drop your campaign image
                            </p>
                            <p className="text-xs text-gray-500">
                              or{" "}
                              <span
                                className="font-semibold"
                                style={{ color: THEME_PRIMARY }}
                              >
                                browse files
                              </span>{" "}
                              - JPG, PNG up to 5 MB
                            </p>
                          </div>
                        )}
                        <input
                          ref={imageInputRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleImageUpload}
                        />
                      </div>
                    )}

                    {activeMediaTab === "video" && (
                      <div
                        className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer hover:bg-gray-50 transition-colors group relative overflow-hidden"
                        style={{ borderColor: THEME_PRIMARY_RING }}
                        onClick={() => videoInputRef.current?.click()}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.borderColor = THEME_PRIMARY)
                        }
                        onMouseLeave={(e) =>
                        (e.currentTarget.style.borderColor =
                          THEME_PRIMARY_RING)
                        }
                      >
                        {videoPreview ? (
                          <div className="relative inline-block w-full">
                            <video
                              src={videoPreview}
                              className="max-h-48 mx-auto rounded-lg object-contain"
                              controls
                              onClick={(e) => e.stopPropagation()}
                            />
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeCampaignVideo();
                              }}
                              className="absolute -top-2 -right-2 w-8 h-8 flex items-center justify-center rounded-full bg-red-600 text-white shadow-md hover:bg-red-700 transition-colors z-10"
                              aria-label="Remove video"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            </button>
                          </div>
                        ) : (
                          <div className="py-4">
                            <div
                              className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform"
                              style={{ backgroundColor: THEME_PRIMARY_SOFT }}
                            >
                              <svg
                                className="w-6 h-6"
                                style={{ color: THEME_PRIMARY }}
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                />
                              </svg>
                            </div>
                            <p className="text-sm font-medium text-gray-900 mb-1">
                              Drag and drop your campaign video
                            </p>
                            <p className="text-xs text-gray-500">
                              or{" "}
                              <span
                                className="font-semibold"
                                style={{ color: THEME_PRIMARY }}
                              >
                                browse files
                              </span>{" "}
                              - MP4 up to 20 MB
                            </p>
                          </div>
                        )}
                        <input
                          ref={videoInputRef}
                          type="file"
                          accept="video/*"
                          className="hidden"
                          onChange={handleVideoUpload}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* RIGHT COLUMN */}
                <div className="space-y-5">
                  {/* Schedule & Visibility */}
                  <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-1">
                      <svg
                        className="w-5 h-5"
                        style={{ color: THEME_PRIMARY }}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <h4 className="text-base font-semibold text-gray-900">
                        Schedule &amp; Visibility
                      </h4>
                    </div>
                    <p className="text-xs text-gray-500 mb-4">
                      Choose when your campaign goes live and who can see it.
                    </p>

                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Start date
                          </label>
                          <input
                            type="date"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm shadow-sm"
                            value={formData.startDate}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                startDate: e.target.value,
                              }))
                            }
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            End date
                          </label>
                          <input
                            type="date"
                            min={formData.startDate || undefined}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm shadow-sm"
                            value={formData.endDate}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                endDate: e.target.value,
                              }))
                            }
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Status
                        </label>
                        <select
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm shadow-sm"
                          value={formData.status}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              status: e.target.value as
                                | "Active"
                                | "Archived",
                            }))
                          }
                        >
                          <option value="Active">Active</option>
                          <option value="Archived">Archived</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Related Content */}
                  <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm space-y-4">
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                        <svg
                          className="w-4 h-4"
                          style={{ color: THEME_PRIMARY }}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                          />
                        </svg>
                        Link events (optional)
                      </label>
                      <p className="text-xs text-gray-500 mb-2">
                        Link related events to this campaign.
                      </p>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setShowEventPicker(true)}
                          className="flex-1 flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm text-left text-gray-500 hover:border-gray-400 transition-colors shadow-sm"
                        >
                          <span className="truncate">
                            {(formData.eventIds || []).length > 0
                              ? `${formData.eventIds?.length} event(s) selected`
                              : "Choose related events"}
                          </span>
                          <svg
                            className="w-4 h-4 text-gray-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowEventPicker(true)}
                          className="px-3 py-2 rounded-lg border transition-colors shadow-sm shrink-0"
                          style={{
                            backgroundColor: THEME_PRIMARY_SOFT,
                            borderColor: THEME_PRIMARY_RING,
                            color: THEME_PRIMARY,
                          }}
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 4v16m8-8H4"
                            />
                          </svg>
                        </button>
                      </div>
                      {(formData.eventIds || []).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {(formData.eventIds || []).map((id) => {
                            const event = events.find((e) => e.id === id);
                            if (!event) return null;
                            return (
                              <span
                                key={id}
                                className="inline-flex items-center max-w-full gap-1.5 px-2.5 py-1 bg-gray-50 border border-gray-200 text-gray-700 rounded-md text-xs font-medium"
                              >
                                <span className="truncate max-w-[180px]">
                                  {event.eventName}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => removeSelectedEvent(id)}
                                  className="text-gray-400 hover:text-red-500 shrink-0"
                                >
                                  <svg
                                    className="w-3.5 h-3.5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M6 18L18 6M6 6l12 12"
                                    />
                                  </svg>
                                </button>
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div className="pt-4 border-t border-gray-100">
                      <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                        <svg
                          className="w-4 h-4"
                          style={{ color: THEME_PRIMARY }}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                          />
                        </svg>
                        Link assets (optional)
                      </label>
                      <p className="text-xs text-gray-500 mb-2">
                        Select product assets to include in this campaign.
                      </p>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            loadCampaignAssets();
                            setShowAssetPicker(true);
                          }}
                          className="flex-1 flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm text-left text-gray-500 hover:border-gray-400 transition-colors shadow-sm"
                        >
                          <span className="truncate">
                            {(formData.assetIds || []).length > 0
                              ? `${formData.assetIds?.length} asset(s) selected`
                              : "Select product assets"}
                          </span>
                          <svg
                            className="w-4 h-4 text-gray-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            loadCampaignAssets();
                            setShowAssetPicker(true);
                          }}
                          className="px-3 py-2 rounded-lg border transition-colors shadow-sm shrink-0"
                          style={{
                            backgroundColor: THEME_PRIMARY_SOFT,
                            borderColor: THEME_PRIMARY_RING,
                            color: THEME_PRIMARY,
                          }}
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 4v16m8-8H4"
                            />
                          </svg>
                        </button>
                      </div>
                      {(formData.assetIds || []).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {(formData.assetIds || []).map((id) => {
                            const asset = findAssetById(id);
                            if (!asset) return null;
                            return (
                              <span
                                key={id}
                                className="inline-flex items-center max-w-full gap-1.5 px-2.5 py-1 bg-gray-50 border border-gray-200 text-gray-700 rounded-md text-xs font-medium"
                              >
                                <span className="truncate max-w-[180px]">
                                  {asset.original_filename}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => removeSelectedAsset(id)}
                                  className="text-gray-400 hover:text-red-500 shrink-0"
                                >
                                  <svg
                                    className="w-3.5 h-3.5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M6 18L18 6M6 6l12 12"
                                    />
                                  </svg>
                                </button>
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 bg-white flex flex-col sm:flex-row items-center justify-between gap-3 shrink-0">
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <span className="text-red-500 font-bold text-sm">*</span>{" "}
                Required fields
                <span className="mx-1">•</span>
                You can edit these details later.
              </div>
              <div className="flex gap-3 w-full sm:w-auto">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 sm:flex-none px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 hover:text-gray-900 transition-colors shadow-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateCampaign}
                  disabled={saving}
                  className="flex-1 sm:flex-none px-5 py-2.5 text-white rounded-xl disabled:opacity-70 disabled:cursor-not-allowed transition-opacity font-medium text-sm shadow-sm flex items-center justify-center gap-2"
                  style={{ backgroundColor: THEME_PRIMARY }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.opacity = "0.9")
                  }
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                >
                  {saving ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-1 h-4 w-4 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Creating...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                      Create Campaign
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Campaign Modal */}
      {showEditModal && selectedCampaign && !showAssetPicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 z-10 bg-white px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800">
                    Edit Campaign
                  </h3>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleDeleteCampaign(selectedCampaign)}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    Delete
                  </button>
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="text-black hover:text-black text-4xl font-bold leading-none"
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Media
                    </label>
                    <div className="flex gap-2 mb-3">
                      <button
                        type="button"
                        onClick={() => setActiveMediaTab("image")}
                        className={`px-4 py-2 rounded transition ${activeMediaTab === "image"
                          ? "bg-purple-600 text-white"
                          : "bg-gray-200"
                          }`}
                      >
                        📷 Image
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveMediaTab("video")}
                        className={`px-4 py-2 rounded transition ${activeMediaTab === "video"
                          ? "bg-purple-600 text-white"
                          : "bg-gray-200"
                          }`}
                      >
                        🎥 Video
                      </button>
                    </div>
                    {activeMediaTab === "image" && (
                      <div
                        className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer"
                        onClick={() => imageInputRef.current?.click()}
                      >
                        {imagePreview ? (
                          <div className="relative inline-block">
                            <img
                              src={imagePreview}
                              alt="Preview"
                              className="max-h-48 mx-auto rounded"
                            />
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeCampaignImage();
                              }}
                              className="absolute -top-2 -right-2 w-7 h-7 flex items-center justify-center rounded-full bg-red-600 text-white text-lg leading-none shadow hover:bg-red-700"
                              aria-label="Remove image"
                              title="Remove image"
                            >
                              ×
                            </button>
                          </div>
                        ) : (
                          <p className="text-gray-500">Click to upload image</p>
                        )}
                        <input
                          ref={imageInputRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleImageUpload}
                        />
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Title *
                    </label>

                    <input
                      type="date"
                      className="w-full p-2 border rounded"
                      value={eventFormData.eventDate}
                      onChange={(e) =>
                        setEventFormData({
                          ...eventFormData,
                          eventDate: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Notes
                    </label>
                    <textarea
                      rows={3}
                      className="w-full p-2 border rounded"
                      value={formData.notes}
                      onChange={(e) =>
                        setFormData({ ...formData, notes: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Description
                    </label>
                    <textarea
                      rows={2}
                      className="w-full p-2 border rounded"
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          description: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
                <div className="space-y-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold mb-3">Schedule</h3>
                    <input
                      type="date"
                      className="w-full p-2 border rounded mb-2"
                      value={formData.startDate}
                      onChange={(e) =>
                        setFormData({ ...formData, startDate: e.target.value })
                      }
                    />
                    <input
                      type="date"
                      min={formData.startDate || undefined}
                      className="w-full p-2 border rounded"
                      value={formData.endDate}
                      onChange={(e) =>
                        setFormData({ ...formData, endDate: e.target.value })
                      }
                    />
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold mb-3">Status</h3>
                    <select
                      className="w-full p-2 border rounded"
                      value={formData.status}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          status: e.target.value as "Active" | "Archived",
                        })
                      }
                    >
                      <option value="Active">Active</option>
                      <option value="Archived">Archived</option>
                    </select>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold mb-3">Tags</h3>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {formData.tags?.map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-gray-200 text-gray-700 text-sm rounded-full"
                        >
                          #{tag}
                          <button onClick={() => removeTag(tag)}>×</button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && addTag()}
                        className="flex-1 px-3 py-2 border rounded-lg"
                        placeholder="Add tag..."
                      />
                      <button
                        onClick={addTag}
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Events Section */}
                    <div className="bg-gray-50 p-4 rounded-lg min-w-0">
                      <div className="flex items-center justify-between mb-3">
                        <button
                          type="button"
                          onClick={() => setShowEventPicker(true)}
                          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition flex items-center gap-2 text-sm"
                        >
                          <span className="text-lg">+</span> Link Events
                        </button>
                      </div>
                      <div className="border border-gray-300 rounded-lg p-3 min-h-[48px] max-h-32 overflow-y-auto flex flex-wrap gap-2 items-start bg-white w-full">
                        {(formData.eventIds || []).length === 0 && (
                          <span className="text-gray-400 text-sm">
                            No events linked
                          </span>
                        )}
                        {(formData.eventIds || []).map((id) => {
                          const event = events.find((e) => e.id === id);
                          if (!event) return null;
                          return (
                            <span
                              key={id}
                              className="inline-flex items-center max-w-full gap-2 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm"
                            >
                              <span className="truncate max-w-[220px]">
                                {event.eventName}
                              </span>
                              <button
                                type="button"
                                onClick={() => removeSelectedEvent(id)}
                                className="text-purple-500 hover:text-purple-700 shrink-0"
                              >
                                ×
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    </div>

                    {/* Assets Section */}
                    <div className="bg-gray-50 p-4 rounded-lg min-w-0">
                      <div className="flex items-center justify-between mb-3">
                        <button
                          type="button"
                          onClick={() => {
                            loadCampaignAssets();
                            setShowAssetPicker(true);
                          }}
                          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition flex items-center gap-2 text-sm"
                        >
                          <span className="text-lg">+</span> Link Assets
                        </button>
                      </div>
                      <div className="border border-gray-300 rounded-lg p-3 min-h-[48px] max-h-32 overflow-y-auto flex flex-wrap gap-2 items-start bg-white w-full">
                        {(formData.assetIds || []).length === 0 && (
                          <span className="text-gray-400 text-sm">
                            No assets linked
                          </span>
                        )}
                        {(formData.assetIds || []).map((id) => {
                          const asset = findAssetById(id);
                          if (!asset) return null;
                          return (
                            <span
                              key={id}
                              className="inline-flex items-center max-w-full gap-2 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm"
                            >
                              <span className="truncate max-w-[220px]">
                                {asset.original_filename}
                              </span>
                              <button
                                type="button"
                                onClick={() => removeSelectedAsset(id)}
                                className="text-purple-500 hover:text-purple-700 shrink-0"
                              >
                                ×
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="sticky bottom-0 z-10 bg-white px-6 py-4 border-t border-gray-200 flex gap-3 justify-end">
              <button
                onClick={() => setShowEditModal(false)}
                disabled={saving}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateCampaign}
                disabled={saving}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 min-w-[140px]"
              >
                {saving && (
                  <span
                    className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
                    aria-hidden="true"
                  />
                )}
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAssetPicker && (
        <AssetPickerModal
          assets={campaignAssets}
          assetsLoading={campaignAssetsLoading}
          productGroups={productGroups}
          productGroupsLoading={productGroupsLoading}
          onSetMany={setAssetsSelection}
          selectedIds={formData.assetIds || []}
          onToggle={toggleAssetSelection}
          onClose={() => setShowAssetPicker(false)}
          searchQuery={assetSearchQuery}
          setSearchQuery={setAssetSearchQuery}
        />
      )}

      {showEventPicker && (
        <EventPickerModal
          events={events}
          selectedIds={formData.eventIds || []}
          onToggle={toggleEventSelection}
          onClose={() => setShowEventPicker(false)}
          searchQuery={eventSearchQuery}
          setSearchQuery={setEventSearchQuery}
        />
      )}

      {/* Campaign Details Modal */}
      {showDetailsModal && selectedCampaign && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-gray-800">
                  Campaign Details
                </h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-black hover:text-black text-4xl font-bold leading-none"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="p-6">
              {selectedCampaign.image && (
                <img
                  src={selectedCampaign.image}
                  alt={selectedCampaign.title}
                  className="w-full h-64 object-cover rounded-lg mb-6"
                />
              )}
              <h2 className="text-2xl font-bold text-gray-800 mb-4">
                {selectedCampaign.title}
              </h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="font-medium text-gray-600">Start Date:</span>
                  <span className="text-gray-800">
                    {formatDate(selectedCampaign.startDate)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="font-medium text-gray-600">End Date:</span>
                  <span className="text-gray-800">
                    {formatDate(selectedCampaign.endDate)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="font-medium text-gray-600">Status:</span>
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${getStatusColor(selectedCampaign.status)}`}
                  >
                    {selectedCampaign.status}
                  </span>
                </div>
                <div className="py-2 border-b">
                  <span className="font-medium text-gray-600">Notes:</span>
                  <p className="text-gray-800 mt-1">{selectedCampaign.notes}</p>
                </div>
                {selectedCampaign.tags && selectedCampaign.tags.length > 0 && (
                  <div className="py-2">
                    <span className="font-medium text-gray-600">Tags:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedCampaign.tags.map((tag) => (
                        <span key={tag} className="text-xs text-gray-400">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Product Modal */}
      {showProductModal && !showProductAssetPicker && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 sm:p-6">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col shadow-xl overflow-hidden">

            {/* Header */}
            <div className="px-6 py-5 border-b border-gray-100 flex items-start justify-between shrink-0 bg-white">
              <div className="flex items-center gap-4">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 border"
                  style={{
                    backgroundColor: THEME_PRIMARY_SOFT,
                    borderColor: THEME_PRIMARY_RING,
                    color: THEME_PRIMARY,
                  }}
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {selectedProduct ? "Edit Product" : "Create Product"}
                  </h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {selectedProduct
                      ? "Update the details for this product"
                      : "Add a new product to your catalog"}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowProductModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-2 -mr-2 rounded-full hover:bg-gray-100"
                aria-label="Close"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="p-5 sm:p-6 overflow-y-auto flex-1 bg-gray-50/30 space-y-5">

              {/* Product Details Card */}
              <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <svg
                    className="w-5 h-5"
                    style={{ color: THEME_PRIMARY }}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <h4 className="text-base font-semibold text-gray-900">
                    Product Details
                  </h4>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Product Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm shadow-sm focus:outline-none"
                      onFocus={(e) => {
                        e.currentTarget.style.boxShadow = `0 0 0 2px ${THEME_PRIMARY_RING}`;
                        e.currentTarget.style.borderColor = THEME_PRIMARY;
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.boxShadow = "";
                        e.currentTarget.style.borderColor = "";
                      }}
                      value={productFormData.name}
                      onChange={(e) =>
                        setProductFormData({
                          ...productFormData,
                          name: e.target.value,
                        })
                      }
                      placeholder="e.g. Summer Collection 2024"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Launch Date
                      </label>
                      <input
                        type="date"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm shadow-sm focus:outline-none"
                        onFocus={(e) => {
                          e.currentTarget.style.boxShadow = `0 0 0 2px ${THEME_PRIMARY_RING}`;
                          e.currentTarget.style.borderColor = THEME_PRIMARY;
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.boxShadow = "";
                          e.currentTarget.style.borderColor = "";
                        }}
                        value={productFormData.launchDate}
                        onChange={(e) =>
                          setProductFormData({
                            ...productFormData,
                            launchDate: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        End Date
                      </label>
                      <input
                        type="date"
                        min={productFormData.launchDate || undefined}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm shadow-sm focus:outline-none"
                        onFocus={(e) => {
                          e.currentTarget.style.boxShadow = `0 0 0 2px ${THEME_PRIMARY_RING}`;
                          e.currentTarget.style.borderColor = THEME_PRIMARY;
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.boxShadow = "";
                          e.currentTarget.style.borderColor = "";
                        }}
                        value={productFormData.endDate || ""}
                        onChange={(e) =>
                          setProductFormData({
                            ...productFormData,
                            endDate: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Description
                    </label>
                    <textarea
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm shadow-sm resize-none focus:outline-none"
                      onFocus={(e) => {
                        e.currentTarget.style.boxShadow = `0 0 0 2px ${THEME_PRIMARY_RING}`;
                        e.currentTarget.style.borderColor = THEME_PRIMARY;
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.boxShadow = "";
                        e.currentTarget.style.borderColor = "";
                      }}
                      value={productFormData.description || ""}
                      onChange={(e) =>
                        setProductFormData({
                          ...productFormData,
                          description: e.target.value,
                        })
                      }
                      placeholder="Brief product description"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Notes
                    </label>
                    <textarea
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm shadow-sm resize-none focus:outline-none"
                      onFocus={(e) => {
                        e.currentTarget.style.boxShadow = `0 0 0 2px ${THEME_PRIMARY_RING}`;
                        e.currentTarget.style.borderColor = THEME_PRIMARY;
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.boxShadow = "";
                        e.currentTarget.style.borderColor = "";
                      }}
                      value={productFormData.notes || ""}
                      onChange={(e) =>
                        setProductFormData({
                          ...productFormData,
                          notes: e.target.value,
                        })
                      }
                      placeholder="Internal notes for your team"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Status
                      </label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm shadow-sm focus:outline-none"
                        onFocus={(e) => {
                          e.currentTarget.style.boxShadow = `0 0 0 2px ${THEME_PRIMARY_RING}`;
                          e.currentTarget.style.borderColor = THEME_PRIMARY;
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.boxShadow = "";
                          e.currentTarget.style.borderColor = "";
                        }}
                        value={productFormData.status}
                        onChange={(e) =>
                          setProductFormData({
                            ...productFormData,
                            status: e.target.value as "Active" | "Archived",
                          })
                        }
                      >
                        <option value="Active">Active</option>
                        <option value="Archived">Archived</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Tags (comma separated)
                      </label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm shadow-sm focus:outline-none"
                        onFocus={(e) => {
                          e.currentTarget.style.boxShadow = `0 0 0 2px ${THEME_PRIMARY_RING}`;
                          e.currentTarget.style.borderColor = THEME_PRIMARY;
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.boxShadow = "";
                          e.currentTarget.style.borderColor = "";
                        }}
                        value={productTagsInput}
                        onChange={(e) => {
                          const raw = e.target.value;
                          setProductTagsInput(raw);
                          setProductFormData((prev) => ({
                            ...prev,
                            tags: raw
                              .split(",")
                              .map((t) => t.trim())
                              .filter(Boolean),
                          }));
                        }}
                        placeholder="tag1, tag2, tag3"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Linked Assets Card */}
              <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <svg
                    className="w-5 h-5"
                    style={{ color: THEME_PRIMARY }}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                    />
                  </svg>
                  <h4 className="text-base font-semibold text-gray-900">
                    Linked Assets
                  </h4>
                </div>

                <div className="flex gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setShowProductAssetPicker(true)}
                    className="flex-1 flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm text-left text-gray-500 hover:border-gray-400 transition-colors shadow-sm"
                  >
                    <span className="truncate">
                      {(productFormData.assetIds || []).length > 0
                        ? `${productFormData.assetIds?.length} asset(s) selected`
                        : "Select assets"}
                    </span>
                    <svg
                      className="w-4 h-4 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowProductAssetPicker(true)}
                    className="px-3 py-2 rounded-lg border transition-colors shadow-sm shrink-0"
                    style={{
                      backgroundColor: THEME_PRIMARY_SOFT,
                      borderColor: THEME_PRIMARY_RING,
                      color: THEME_PRIMARY,
                    }}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                  </button>
                </div>

                {(productFormData.assetIds || []).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {(productFormData.assetIds || []).map((id) => {
                      const asset = assets.find((a) => a.id === id);
                      if (!asset) return null;
                      return (
                        <span
                          key={id}
                          className="inline-flex items-center max-w-full gap-1.5 px-2.5 py-1 bg-gray-50 border border-gray-200 text-gray-700 rounded-md text-xs font-medium"
                        >
                          <span className="truncate max-w-[180px]">
                            {asset.original_filename}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeSelectedProductAsset(id)}
                            className="text-gray-400 hover:text-red-500 shrink-0"
                          >
                            <svg
                              className="w-3.5 h-3.5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 bg-white flex flex-col sm:flex-row items-center justify-between gap-3 shrink-0">
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <span className="text-red-500 font-bold text-sm">*</span>{" "}
                Required fields
              </div>
              <div className="flex gap-3 w-full sm:w-auto">
                <button
                  onClick={() => setShowProductModal(false)}
                  disabled={saving}
                  className="flex-1 sm:flex-none px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 hover:text-gray-900 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={
                    selectedProduct ? handleUpdateProduct : handleCreateProduct
                  }
                  disabled={saving}
                  className="flex-1 sm:flex-none px-5 py-2.5 text-white rounded-xl disabled:opacity-70 disabled:cursor-not-allowed transition-opacity font-medium text-sm shadow-sm flex items-center justify-center gap-2"
                  style={{ backgroundColor: THEME_PRIMARY }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.opacity = "0.9")
                  }
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                >
                  {saving ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-1 h-4 w-4 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      {selectedProduct ? "Updating..." : "Creating..."}
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      {selectedProduct ? "Update Product" : "Create Product"}
                    </>
                  )}
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

      {showProductAssetPicker && (
        <AssetPickerModal
          assets={assets}
          assetsLoading={assetsLoading}
          selectedIds={productFormData.assetIds || []}
          onToggle={toggleProductAssetSelection}
          onClose={() => setShowProductAssetPicker(false)}
          searchQuery={productAssetSearchQuery}
          setSearchQuery={setProductAssetSearchQuery}
        />
      )}

      {/* Event page: link products */}
      {showEventProductPicker && (
        <ProductPickerModal
          products={pickerProducts}
          loading={pickerProductsLoading}
          selectedIds={eventFormData.productIds || []}
          onToggle={(id) => toggleEventLink("productIds", id)}
          onClose={() => setShowEventProductPicker(false)}
          searchQuery={eventProductSearchQuery}
          setSearchQuery={setEventProductSearchQuery}
        />
      )}

      {/* Event page: link assets */}
      {showEventAssetPicker && (
        <AssetPickerModal
          assets={assets}
          assetsLoading={assetsLoading}
          selectedIds={eventFormData.assetIds || []}
          onToggle={(id) => toggleEventLink("assetIds", id)}
          onClose={() => setShowEventAssetPicker(false)}
          searchQuery={eventAssetSearchQuery}
          setSearchQuery={setEventAssetSearchQuery}
        />
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable
        theme="colored"
      />
    </div>
  );
};

export default CampaignsAndEvents;